import React, { useState, useEffect, useMemo, useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import AddRecipientsModal from "./AddRecipientsModal";
import ComplianceReportModal from "./ComplianceReportModal";
import GenerateCsvReportModal from "./GenerateCsvReportModal";
import { useScheduleContext, ScheduleProvider } from "./ScheduleContext";
import ScheduleBreadcrumb from "./ScheduleBreadcrumb";
import format from "date-fns/format";
import { differenceInSeconds, sub } from "date-fns";
import TextEditor from "new_components/organisms/TextEditor";
import { createPortal } from "react-dom";
import PublicLink from "components/CultureAudit/PublicLink";

import {
  SG_GET_DEBRIEF_TOKENS,
  SG_FETCH_DEBRIEF_SCHEDULE,
  SG_GET_AUDIT_ELIGIBLE,
  SG_GET_EMPLOYEE_ORG_CATEGORIES,
  SG_EXTEND_SURVEY_TIME,
  SG_EDIT_DEBRIEF_SCHEDULE,
  SG_ADD_DEBRIEF_SCHEDULE_ADD_USERS_BULK,
  SG_DELETE_DEBRIEF_SCHEDULE,
  SG_FETCH_ORGANIZATION_GROUP,
  SG_BULK_UPDATE_ORGANIZATION_GROUP,
  SG_ADD_DEBRIEF_EMAIL,
  SG_GET_DEBRIEF_EMAIL,
  SG_DELETE_DEBRIEF_EMAIL,
  SG_EDIT_DEBRIEF_TOKEN,
} from "constants/actions";

const CalendarIcon = () => (
  <svg
      width="15"
      height="15"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1_158)">
        <path
          d="M3.02222 20H16.9778C18.4407 20 19.6296 18.8111 19.6296 17.3481V4.13331C19.6296 2.67035 18.4407 1.48145 16.9778 1.48145H15.9259V0.740727C15.9259 0.333315 15.5926 0 15.1851 0C14.7777 0 14.4444 0.333315 14.4444 0.740727V1.48145H5.55557V0.740727C5.55557 0.333315 5.2222 0 4.81479 0C4.40737 0 4.07406 0.333315 4.07406 0.740727V1.48145H3.02222C1.55926 1.48145 0.370361 2.67035 0.370361 4.13331V17.3481C0.370361 18.8111 1.55926 20 3.02222 20ZM1.85188 4.13331C1.85188 3.48889 2.3778 2.96297 3.02222 2.96297H4.07406V3.7037C4.07406 4.11111 4.40737 4.44442 4.81479 4.44442C5.2222 4.44442 5.55551 4.11111 5.55551 3.7037V2.96297H14.4444V3.7037C14.4444 4.11111 14.7777 4.44442 15.1851 4.44442C15.5926 4.44442 15.9259 4.11111 15.9259 3.7037V2.96297H16.9777C17.6221 2.96297 18.1481 3.48889 18.1481 4.13331V17.3481C18.1481 17.9926 17.6221 18.5185 16.9777 18.5185H3.02222C2.3778 18.5185 1.85188 17.9926 1.85188 17.3481V4.13331Z"
          fill="currentColor"
        />
        <path
          d="M5.74085 8.51847C6.25222 8.51847 6.66676 8.10393 6.66676 7.59256C6.66676 7.0812 6.25222 6.66666 5.74085 6.66666C5.22948 6.66666 4.81494 7.0812 4.81494 7.59256C4.81494 8.10393 5.22948 8.51847 5.74085 8.51847Z"
          fill="currentColor"
        />
        <path
          d="M9.81482 8.51847C10.3262 8.51847 10.7407 8.10393 10.7407 7.59256C10.7407 7.0812 10.3262 6.66666 9.81482 6.66666C9.30346 6.66666 8.88892 7.0812 8.88892 7.59256C8.88892 8.10393 9.30346 8.51847 9.81482 8.51847Z"
          fill="currentColor"
        />
        <path
          d="M13.8888 8.51847C14.4002 8.51847 14.8147 8.10393 14.8147 7.59256C14.8147 7.0812 14.4002 6.66666 13.8888 6.66666C13.3774 6.66666 12.9629 7.0812 12.9629 7.59256C12.9629 8.10393 13.3774 8.51847 13.8888 8.51847Z"
          fill="currentColor"
        />
        <path
          d="M5.74085 12.2222C6.25222 12.2222 6.66676 11.8076 6.66676 11.2963C6.66676 10.7849 6.25222 10.3704 5.74085 10.3704C5.22948 10.3704 4.81494 10.7849 4.81494 11.2963C4.81494 11.8076 5.22948 12.2222 5.74085 12.2222Z"
          fill="currentColor"
        />
        <path
          d="M9.81482 12.2222C10.3262 12.2222 10.7407 11.8076 10.7407 11.2963C10.7407 10.7849 10.3262 10.3704 9.81482 10.3704C9.30346 10.3704 8.88892 10.7849 8.88892 11.2963C8.88892 11.8076 9.30346 12.2222 9.81482 12.2222Z"
          fill="currentColor"
        />
        <path
          d="M13.8888 12.2222C14.4002 12.2222 14.8147 11.8076 14.8147 11.2963C14.8147 10.7849 14.4002 10.3704 13.8888 10.3704C13.3774 10.3704 12.9629 10.7849 12.9629 11.2963C12.9629 11.8076 13.3774 12.2222 13.8888 12.2222Z"
          fill="currentColor"
        />
        <path
          d="M5.74085 15.9259C6.25222 15.9259 6.66676 15.5113 6.66676 15C6.66676 14.4886 6.25222 14.0741 5.74085 14.0741C5.22948 14.0741 4.81494 14.4886 4.81494 15C4.81494 15.5113 5.22948 15.9259 5.74085 15.9259Z"
          fill="currentColor"
        />
        <path
          d="M9.81482 15.9259C10.3262 15.9259 10.7407 15.5113 10.7407 15C10.7407 14.4886 10.3262 14.0741 9.81482 14.0741C9.30346 14.0741 8.88892 14.4886 8.88892 15C8.88892 15.5113 9.30346 15.9259 9.81482 15.9259Z"
          fill="currentColor"
        />
        <path
          d="M13.8888 15.9259C14.4002 15.9259 14.8147 15.5113 14.8147 15C14.8147 14.4886 14.4002 14.0741 13.8888 14.0741C13.3774 14.0741 12.9629 14.4886 12.9629 15C12.9629 15.5113 13.3774 15.9259 13.8888 15.9259Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_1_158">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
);

const SettingsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11.03L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11.03C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
);

const UserPlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15,14C12.33,14 7,15.33 7,18V20H23V18C23,15.33 17.67,14 15,14M6,10V7H4V10H1V12H4V15H6V12H9V10M15,12A4,4 0 0,0 19,8A4,4 0 0,0 15,4A4,4 0 0,0 11,8A4,4 0 0,0 15,12Z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.7L16.2,16.2Z" />
  </svg>
);

const WarningIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M1 21h22L12 2 1 21zm12-3h-2v2h2v-2zm0-4h-2v4h2v-4z" />
  </svg>
);

const MoreVertIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,16A2,2 0 0,1 14,18A2,2 0 0,1 12,20A2,2 0 0,1 10,18A2,2 0 0,1 12,16M12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12A2,2 0 0,1 12,10M12,4A2,2 0 0,1 14,6A2,2 0 0,1 12,8A2,2 0 0,1 10,6A2,2 0 0,1 12,4Z" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
  </svg>
);

const SortIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 21L14 17H17V7H14L18 3L22 7H19V17H22L18 21Z" />
    <path d="M2 19V17H12V19H2Z" />
    <path d="M2 13V11H9V13H2Z" />
    <path d="M2 7V5H6V7H2Z" />
  </svg>
);

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M14,12V19.88C14.04,20.18 13.94,20.5 13.71,20.71C13.32,21.1 12.69,21.1 12.3,20.71L10.29,18.7C10.06,18.47 9.96,18.16 10,17.87V12H9.97L4.21,4.62C3.87,4.19 3.95,3.56 4.38,3.22C4.57,3.08 4.78,3 5,3V3H19V3C19.22,3 19.43,3.08 19.62,3.22C20.05,3.56 20.13,4.19 19.79,4.62L14.03,12H14Z" />
  </svg>
);

// Simple portal wrapper to render floating menus at the document body level
const PopoverPortal = ({ children }) => {
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
};

const toNum = (value, fallback = 0) => {
  const numeric = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(numeric) ? numeric : fallback;
};

const ManageActiveSurveyContent = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Survey ID from URL
  const location = useLocation();
  const { addToast } = useToasts();
  const dispatch = useDispatch();
  const [optionsAnchor, setOptionsAnchor] = useState(null);
  const optionsButtonRef = useRef(null);
  const optionsMenuRef = useRef(null);
  const browserTimeZone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (err) {
      return "UTC";
    }
  }, []);
  
  // Function to get the base URL for survey links based on current environment
  const getSurveyBaseUrl = () => {
    const currentHost = window.location.host;
    
    if (currentHost.includes('stg-app.innerlogic')) {
      return 'https://stg-survey.innerlogic.com';
    } else if (currentHost.includes('demo.innerlogic')) {
      return 'https://demo-survey.innerlogic.com';
    } else if (currentHost.includes('localhost')) {
      return 'https://stg-survey.innerlogic.com';
    } else if (currentHost.includes('app.innerlogic')) {
      return 'https://survey.innerlogic.com';
    }
    
    // Default fallback
    return 'https://survey.innerlogic.com';
  };

  // Function to copy survey link to clipboard
  const copySurveyLink = async (recipientToken) => {
    if (!recipientToken) {
      addToast('Unable to copy survey link - no token found', { appearance: 'error' });
      return;
    }
    
    const baseUrl = getSurveyBaseUrl();
    const surveyUrl = `${baseUrl}/culture-audit-v2/${recipientToken}`;
    
    try {
      await navigator.clipboard.writeText(surveyUrl);
      addToast('Survey link copied to clipboard!', { appearance: 'success' });
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = surveyUrl;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        addToast('Survey link copied to clipboard!', { appearance: 'success' });
      } catch (fallbackErr) {
        addToast('Failed to copy survey link', { appearance: 'error' });
      }
      document.body.removeChild(textArea);
    }
  };

  const sendReminderToRecipient = (recipient) => {
    if (!recipient) {
      return;
    }

    const tokens = Array.isArray(get_debrief_tokens)
      ? get_debrief_tokens.filter((token) => token && !token.to_delete)
      : [];

    const participantTokenId = Number(recipient.participantId);
    let token = Number.isFinite(participantTokenId)
      ? tokens.find((t) => Number(t?.id) === participantTokenId)
      : null;

    if (!token && recipient.employeeId) {
      token = tokens.find((t) => t?.employee === recipient.employeeId);
    }

    if (!token?.id) {
      addToast('Unable to send reminder - no active survey link found for this recipient.', {
        appearance: 'error',
      });
      return;
    }

    dispatch({
      type: SG_EDIT_DEBRIEF_TOKEN,
      payload: {
        id: token.id,
        remind: token.id,
      },
    });

    addToast('Reminder queued. Emails typically send within about 5 minutes.', {
      appearance: 'success',
    });

    // Refresh token data shortly after the reminder to update reminder counts
    setTimeout(() => {
      if (id) {
        dispatch({ type: SG_GET_DEBRIEF_TOKENS, payload: `debrief_schedule=${id}` });
      }
    }, 2000);
  };

  const determineRowMenuPlacement = (domRect) => {
    if (!domRect) {
      return 'down';
    }

    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
    const estimatedMenuHeight = 260;
    const spaceBelow = viewportHeight ? viewportHeight - domRect.bottom : 0;
    const spaceAbove = domRect.top;

    if (viewportHeight && spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow) {
      return 'up';
    }

    return 'down';
  };

  // Handle row menu actions
  const handleRowMenuClick = (action, recipient) => {
    setActiveRowMenu(null);

    if (isSurveyClosed) {
      return;
    }

    switch (action) {
      case 'copy-link': {
        // Find the token for this recipient from debrief_tokens
        const recipientToken = get_debrief_tokens.find(
          (token) => token.employee === recipient.employeeId
        );
        copySurveyLink(recipientToken?.token);
        break;
      }
      case 'remind':
        sendReminderToRecipient(recipient);
        break;
      case 'remove':
        handleRemoveRecipient(recipient.id);
        break;
      default:
        break;
    }
  };

  // Get data from ScheduleContext
  const { deleteSurveySchedule } = useScheduleContext();

  // Get survey data directly from Redux (same as SurveyStatusPending.js)
  const {
    get_auth,
    get_organizations,
    get_debrief_schedules,
    get_debrief_tokens,
    get_employees,
    get_employee_org_category,
    get_organization_groups,
    get_survey_structure,
    get_employee,
    get_debrief_emails,
  } = useSelector(
    (state) => ({
      get_auth: state.auth,
      get_organizations: state.organizations,
      get_debrief_schedules: state.debrief_schedule,
      get_debrief_tokens: state.debrief_token?.debrief_token || [],
      get_employees: state.employees?.employees || [],
      get_employee_org_category:
        state.employee_category?.employee_org_category || [],
      get_organization_groups: state.organization_group,
      get_survey_structure: state.surveystructure?.survey_structure || [],
      get_employee: state.employees,
      get_debrief_emails: state.debrief_email?.debrief_email || [],
    }),
    shallowEqual
    );
  
  // Local state for participants (same as SurveyStatusPending.js)
  const [participants, setParticipants] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(true);
  const [debriefSchedule, setDebriefSchedule] = useState([]);
  const [completedEmpId, setCompletedEmpId] = useState([]);
  const [auditEligibleList, setAuditEligibleList] = useState(false);

  // State for recipients table
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recipientsPerPage] = useState(10);
  const [pageInputValue, setPageInputValue] = useState("1");
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeRowMenu, setActiveRowMenu] = useState(null);
  const [recipientsTableLoading, setRecipientsTableLoading] = useState(true);
  // Track token readiness to avoid status flicker while tokens hydrate
  const [tokensReady, setTokensReady] = useState(false);
  const requestedTokensForIdRef = React.useRef(null);
  const lastTokensRef = React.useRef(null);

  // State for sorting and filtering
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "completed", "pending", "in_progress", "email_bounced"

  // State for confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recipientToDelete, setRecipientToDelete] = useState(null);

  // State for options menu modals
  const [showExtendDeadlineModal, setShowExtendDeadlineModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showCustomReminderModal, setShowCustomReminderModal] = useState(false);
  const [showSendReminderModal, setShowSendReminderModal] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);

  // State for cancel modal
  const [showCancelSurveyModal, setShowCancelSurveyModal] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);

  // State for compliance report modal
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // State for CSV report modal loading
  const [csvModalLoading, setCsvModalLoading] = useState(false);

  // State for email status modal
  const [showEmailStatusModal, setShowEmailStatusModal] = useState(false);

  // State for public link modal
  const [showPublicLink, setShowPublicLink] = useState(false);

  // State for edit name modal
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [editNameData, setEditNameData] = useState({
    name: "",
  });

  // Timer state for reminder cooldown (same as SurveyStatusPending.js)
  const [timeLeft, setTimeLeft] = useState(0);

  // State for extend deadline form
  const [extendDeadlineData, setExtendDeadlineData] = useState({
    newEndDate: "",
    newEndTime: "",
    quickOption: "",
  });

  // State for reschedule form
  const [rescheduleData, setRescheduleData] = useState({
    newStartDate: "",
    newStartTime: "",
    quickOption: "",
  });

  // State for custom reminder form
  const [customReminderData, setCustomReminderData] = useState({
    subject: "",
    body: "<p></p>",
    scheduledDate: "",
    scheduledTime: "",
    sendNow: false,
  });

  // Get organization ID
  const organizationId = get_auth?.organization_id;

  // Find the specific survey by ID from real data
  const surveyData = useMemo(() => {
    const debriefSchedules =
      get_debrief_schedules?.debrief_schedule?.results || [];
    const detailed = get_debrief_schedules?.[id];
    const survey = debriefSchedules.find((brief) => brief.id.toString() === id);

    if (survey) {
      // Find the survey structure name
      const surveyStructureId = survey.survey_structure;
      const surveyStructure = Array.isArray(get_survey_structure) 
        ? get_survey_structure.find(structure => structure.id === surveyStructureId)
        : null;
      const surveyStructureName = surveyStructure?.name || "Culture Audit";
      
      // Use local debriefSchedule name if available (for optimistic updates), otherwise use survey name
      const scheduleName = debriefSchedule?.name 
        ? (typeof debriefSchedule.name === "string" ? debriefSchedule.name : "")
        : (typeof survey.name === "string" ? survey.name : "");

      return {
        id: survey.id,
        name: scheduleName,
        title: scheduleName || survey.name,
        surveyStructureName: surveyStructureName,
        type: "Culture Audit",
        status: survey.status_text?.toLowerCase() || "pending",
        startDate: detailed?.scheduled_for || survey.scheduled_for,
        startTime: (detailed?.scheduled_for || survey.scheduled_for)
          ? format(new Date(detailed?.scheduled_for || survey.scheduled_for), "HH:mm")
          : null,
        endDate: detailed?.close_at || survey.close_at,
        endTime: (detailed?.close_at || survey.close_at)
          ? format(new Date(detailed?.close_at || survey.close_at), "HH:mm")
          : null,
        timezone:
          detailed?.time_zone ||
          survey.time_zone ||
          detailed?.user_time_zone ||
          survey.user_time_zone ||
          browserTimeZone,
        totalRecipients: survey.total_requests || 0,
        completedResponses: survey.total_responses || 0,
        pendingResponses:
          (survey.total_requests || 0) - (survey.total_responses || 0),
        completionRate:
          survey.total_requests > 0
            ? Math.round((survey.total_responses / survey.total_requests) * 100)
            : 0,
        rawData: survey, // Keep reference to raw data for additional fields
      };
    }
    return null; // Return null if survey not found
  }, [
    get_debrief_schedules?.debrief_schedule?.results,
    get_survey_structure,
    id,
    get_debrief_schedules?.[id],
    browserTimeZone,
    debriefSchedule?.name, // Add dependency on local name for optimistic updates
  ]);

  const headerTitle = useMemo(() => {
    const scheduleName =
      typeof surveyData?.name === "string" ? surveyData.name.trim() : "";
    if (scheduleName) return scheduleName;

    const structureName =
      typeof surveyData?.surveyStructureName === "string"
        ? surveyData.surveyStructureName.trim()
        : "";
    if (structureName) return structureName;

    return surveyData?.title || "Survey";
  }, [surveyData?.name, surveyData?.surveyStructureName, surveyData?.title]);

  const breadcrumbCrumbs = useMemo(() => {
    if (!surveyData?.title && !surveyData?.surveyStructureName) {
      return [];
    }
    return [{ label: surveyData?.title || surveyData.surveyStructureName }];
  }, [surveyData?.title, surveyData?.surveyStructureName]);

  const displayTimezone = useMemo(() => {
    const tz = surveyData?.timezone;
    if (!tz) return null;

    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      });
      const parts = formatter.formatToParts(new Date());
      const tzName = parts.find((part) => part.type === "timeZoneName")?.value;
      return tzName || tz;
    } catch (err) {
      return tz;
    }
  }, [surveyData?.timezone]);

  const isSurveyClosed = useMemo(() => {
    const statusCode = Number(surveyData?.rawData?.status);
    if (Number.isFinite(statusCode) && statusCode === 4) {
      return true;
    }

    const statusText = (surveyData?.status || "").toLowerCase();
    return statusText.includes("completed") || statusText.includes("closed");
  }, [surveyData?.rawData?.status, surveyData?.status]);

  const showCsvHack = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("csv_report") === "true";
  }, [location.search]);

  // Derive counts from the token list when active; rely on schedule totals once the survey is complete
  const tokenDerivedCounts = useMemo(() => {
    const fallbackTotal = toNum(
      surveyData?.rawData?.total_requests ?? surveyData?.totalRecipients,
      0
    );
    const fallbackCompleted = toNum(
      surveyData?.rawData?.total_responses ?? surveyData?.completedResponses,
      0
    );
    const fallback = {
      total: fallbackTotal,
      completed: fallbackCompleted,
      pending: Math.max(fallbackTotal - fallbackCompleted, 0),
      completionRate:
        fallbackTotal > 0
          ? Math.round((fallbackCompleted / fallbackTotal) * 100)
          : 0,
    };

    if (isSurveyClosed) {
      return fallback;
    }

    const activeParticipants = participants.filter(
      (participant) => participant && participant.status !== "cancelled"
    );

    if (!tokensReady || activeParticipants.length === 0) {
      return fallback;
    }

    const total = activeParticipants.length;
    const completed = activeParticipants.filter(
      (participant) => participant.status === "completed"
    ).length;
    const pending = Math.max(total - completed, 0);
    const completionRate =
      total > 0 ? Math.round((completed / total) * 100) : 0;

    return { total, pending, completed, completionRate };
  }, [
    isSurveyClosed,
    surveyData?.rawData?.total_requests,
    surveyData?.rawData?.total_responses,
    surveyData?.totalRecipients,
    surveyData?.completedResponses,
    participants,
    tokensReady,
  ]);

  const isHydratingRecipients = useMemo(
    () => !tokensReady || participantsLoading,
    [tokensReady, participantsLoading]
  );

  // Compute whether the survey has started
  const hasStarted = useMemo(() => {
    // Prefer raw scheduled timestamp if available
    const scheduledTs =
      surveyData?.rawData?.scheduled_for || surveyData?.startDate;

    if (!scheduledTs) {
      // Fall back to status if timestamp is absent
      const s = (surveyData?.status || "").toLowerCase();
      return !(s === "pending" || s === "not started");
    }

    // Compare with now
    return new Date() >= new Date(scheduledTs);
  }, [surveyData]);

  useEffect(() => {
    if (!isSurveyClosed) return;

    setShowAddModal(false);
    setShowOptionsMenu(false);
    setOptionsAnchor(null);
    setShowDeleteModal(false);
    setShowExtendDeadlineModal(false);
    setShowRescheduleModal(false);
    setShowCustomReminderModal(false);
    setShowSendReminderModal(false);
    setShowCancelSurveyModal(false);
    setShowComplianceModal(false);
    setShowEmailStatusModal(false);
    setShowPublicLink(false);
    setShowEditNameModal(false);
    setActiveRowMenu(null);
  }, [isSurveyClosed]);

  // Fetch participants data when component mounts (same as SurveyStatusPending.js)
  useEffect(() => {
    if (id) {
      setParticipantsLoading(true);
      setRecipientsTableLoading(true);
      // Fetch schedule details with compliance data
      dispatch({
        type: SG_FETCH_DEBRIEF_SCHEDULE,
        payload: { id: id },
      });
      // Fetch eligible team members
      dispatch({
        type: SG_GET_AUDIT_ELIGIBLE,
        payload: `audit-id=${id}`,
      });
      // Fetch employee org categories if needed
      if (get_auth?.organization_id && !get_employee_org_category?.[0]) {
        dispatch({
          type: SG_GET_EMPLOYEE_ORG_CATEGORIES,
          payload: `organization=${get_auth?.organization_id}`,
        });
      }
    }
  }, [dispatch, id, get_auth?.organization_id, get_employee_org_category]);

  // Prevent flicker when navigating between surveys by resetting local state
  useLayoutEffect(() => {
    // Clear out stale data synchronously before paint when the id changes
    setParticipants([]);
    setParticipantsLoading(true);
    setRecipientsTableLoading(true);
    setActiveRowMenu(null);
    setSearchTerm("");
    setCurrentPage(1);
    setStatusFilter("all");
  }, [id]);

  // Fetch tokens when schedule is loaded
  useEffect(() => {
    if (id && debriefSchedule) {
      // mark tokens as not ready until we observe a token state change for this id
      requestedTokensForIdRef.current = id;
      setTokensReady(false);
      dispatch({
        type: SG_GET_DEBRIEF_TOKENS,
        payload: `debrief_schedule=${id}`,
      });
    }
  }, [dispatch, id, debriefSchedule]);

  // Observe token state changes to determine readiness for the current schedule
  useEffect(() => {
    if (!id) return;
    if (requestedTokensForIdRef.current !== id) return;
    if (lastTokensRef.current !== get_debrief_tokens) {
      lastTokensRef.current = get_debrief_tokens;
      setTokensReady(true);
    }
  }, [id, get_debrief_tokens]);

  // Fetch email data when modal is opened
  useEffect(() => {
    if (showEmailStatusModal && id) {
      dispatch({
        type: SG_GET_DEBRIEF_EMAIL,
        payload: `debrief_schedule=${id}`,
      });
    }
  }, [dispatch, id, showEmailStatusModal]);


  // Track the category length when we start loading
  const loadingStartLengthRef = useRef(null);
  const hasDispatchedRef = useRef(false);

  // Track CSV modal loading state
  const csvLoadingStartLengthRef = useRef(null);
  const csvHasDispatchedRef = useRef(false);

  // Fetch employee org categories with multi-page fetch when compliance modal is opened
  useEffect(() => {
    if (showComplianceModal && get_auth?.organization_id && !hasDispatchedRef.current) {
      // Store current length before we start loading
      loadingStartLengthRef.current = get_employee_org_category?.length || 0;
      setCategoriesLoading(true);
      hasDispatchedRef.current = true;
      dispatch({
        type: SG_GET_EMPLOYEE_ORG_CATEGORIES,
        payload: {
          organization: get_auth?.organization_id,
          fetchAllPages: true,
        },
      });
    } else if (!showComplianceModal) {
      // Reset loading state and refs when modal closes
      setCategoriesLoading(false);
      loadingStartLengthRef.current = null;
      hasDispatchedRef.current = false;
    }
  }, [dispatch, showComplianceModal, get_auth?.organization_id, get_employee_org_category?.length]);

  // Fetch employee org categories with multi-page fetch when CSV modal is opened
  useEffect(() => {
    if (showCsvModal && get_auth?.organization_id && !csvHasDispatchedRef.current) {
      // Store current length before we start loading
      csvLoadingStartLengthRef.current = get_employee_org_category?.length || 0;
      setCsvModalLoading(true);
      csvHasDispatchedRef.current = true;
      dispatch({
        type: SG_GET_EMPLOYEE_ORG_CATEGORIES,
        payload: {
          organization: get_auth?.organization_id,
          fetchAllPages: true,
        },
      });
    } else if (!showCsvModal) {
      // Reset loading state and refs when modal closes
      setCsvModalLoading(false);
      csvLoadingStartLengthRef.current = null;
      csvHasDispatchedRef.current = false;
    }
  }, [dispatch, showCsvModal, get_auth?.organization_id, get_employee_org_category?.length]);

  // Track when categories have finished loading for CSV modal
  useEffect(() => {
    const currentLength = get_employee_org_category?.length || 0;

    // Only complete loading if:
    // 1. We're currently loading
    // 2. We have a start length recorded
    // 3. The current length is different from when we started
    // 4. The current length is greater than 0
    if (
      csvModalLoading &&
      csvLoadingStartLengthRef.current !== null &&
      currentLength > 0 &&
      currentLength !== csvLoadingStartLengthRef.current
    ) {
      setCsvModalLoading(false);
    }

    // Safety timeout - stop loading after 30 seconds even if data doesn't arrive
    if (csvModalLoading) {
      const timeout = setTimeout(() => {
        console.warn('⚠️ CSV modal category loading timeout - forcing completion');
        setCsvModalLoading(false);
      }, 30000);
      return () => clearTimeout(timeout);
    }
  }, [csvModalLoading, get_employee_org_category]);

  // Track when categories have finished loading
  useEffect(() => {
    const currentLength = get_employee_org_category?.length || 0;

    // Only complete loading if:
    // 1. We're currently loading
    // 2. We have a start length recorded
    // 3. The current length is different from when we started
    // 4. The current length is greater than 0
    if (
      categoriesLoading &&
      loadingStartLengthRef.current !== null &&
      currentLength > 0 &&
      currentLength !== loadingStartLengthRef.current
    ) {
      setCategoriesLoading(false);
    }

    // Safety timeout - stop loading after 30 seconds even if data doesn't arrive
    if (categoriesLoading) {
      const timeout = setTimeout(() => {
        console.warn('⚠️ Category loading timeout - forcing completion');
        setCategoriesLoading(false);
      }, 30000);
      return () => clearTimeout(timeout);
    }
  }, [categoriesLoading, get_employee_org_category]);

  useEffect(() => {
    setDebriefSchedule(get_debrief_schedules?.[id]);
    const complianceData = get_debrief_schedules?.[id]?.compliance || [];
    setCompletedEmpId(complianceData);
  }, [id, get_debrief_schedules]);

  useEffect(() => {
    setAuditEligibleList(get_debrief_schedules?.audit_eligible?.response);
  }, [get_debrief_schedules]);

  // Timer logic for reminder cooldown (same as SurveyStatusPending.js)
  useEffect(() => {
    if (debriefSchedule?.updated_at) {
      setTimeLeft(
        differenceInSeconds(
          new Date(debriefSchedule?.updated_at),
          sub(new Date(), {
            minutes: 5,
            seconds: 30,
          })
        )
      );
    }
  }, [debriefSchedule]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer1 = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);

      return () => clearTimeout(timer1);
    }
  });

  // Separate useEffect to fetch organization group when needed
  useEffect(() => {
    if (surveyData?.rawData?.organization_group && 
        (!debriefSchedule?.request_list || debriefSchedule.request_list.length === 0) &&
        (!debriefSchedule?.compliance || debriefSchedule.compliance.length === 0) &&
        get_debrief_tokens.length === 0 &&
        get_organization_groups?.organization_group?.id !== surveyData.rawData.organization_group) {
      
      // Only fetch if we don't already have this organization group loaded
      dispatch({ 
        type: SG_FETCH_ORGANIZATION_GROUP, 
        payload: { id: surveyData.rawData.organization_group } 
      });
    }
  }, [dispatch, surveyData?.rawData?.organization_group, debriefSchedule?.request_list, debriefSchedule?.compliance, get_debrief_tokens.length, get_organization_groups?.organization_group?.id]);

  // Transform into recipients using proper token logic based on request_list and compliance
  useEffect(() => {
    if (!debriefSchedule && !surveyData) {
      return;
    }

    const _request = debriefSchedule?.request_list || surveyData?.rawData?.request_list || [];
    const _compliance = debriefSchedule?.compliance || surveyData?.rawData?.compliance || [];
    const EmployeeList = [];
    const processedEmployees = new Set(); // Track employee IDs to avoid duplicates

    // Build a map of employee ID to their most recent active token (not marked to_delete)
    const employeeToActiveToken = new Map();
    const tokens = Array.isArray(get_debrief_tokens) ? get_debrief_tokens : [];
    
    tokens.forEach((token) => {
      if (token?.employee && !token?.to_delete) {
        const existingToken = employeeToActiveToken.get(token.employee);
        // Keep the most recent token (by updated_at or created_at)
        if (!existingToken || 
            new Date(token.updated_at || token.created_at) > new Date(existingToken.updated_at || existingToken.created_at)) {
          employeeToActiveToken.set(token.employee, token);
        }
      }
    });

    // Helper function to create participant object
    const createParticipant = (employeeId, token, employee, isCompleted = false) => {
      const categories = get_employee_org_category.find(f => f.employee === employeeId);

      // Determine status using token survey_status as the primary source
      const surveyStatus = Number(token?.survey_status);
      let statusFromToken = null;

      if (Number.isFinite(surveyStatus)) {
        switch (surveyStatus) {
          case 3:
            statusFromToken = "completed";
            break;
          case 2:
            statusFromToken = "in_progress"; // Started/In Session
            break;
          case 1:
          case 0:
            statusFromToken = "pending"; // Not Started
            break;
          case 5:
            statusFromToken = "cancelled";
            break;
          case 9:
            statusFromToken = "incomplete";
            break;
          default:
            statusFromToken = null;
        }
      }

      let status = statusFromToken;

      if (!status) {
        status = isCompleted ? "completed" : "pending";
      }

      const deliveryStatus = (token?.delivery_status || "").toLowerCase();
      const emailStatusText = (employee?.email_status_text || "").toLowerCase();
      const emailStatusCode = Number(employee?.email_status);
      const hasBounced =
        status !== "completed" &&
        (deliveryStatus.includes("bounce") ||
          deliveryStatus.includes("spam") ||
          emailStatusText.includes("bounce") ||
          emailStatusText.includes("spam") ||
          [3, 4].includes(emailStatusCode));

      if (hasBounced) {
        status = "email_bounced";
      }

      if (status === "cancelled") {
        return null; // Skip cancelled recipients entirely
      }

      return {
        id: token?.id || `emp_${employeeId}`,
        firstName: employee?.first_name || "N/A",
        lastName: employee?.last_name || "",
        email: employee?.email || "N/A", 
        status: status,
        submittedAt: status === "completed" ? "Completed" : null,
        remindersSent: token?.reminded || 0,
        employeeId: employeeId,
        participantId: token?.id || null,
        employee_status: employee?.status,
        delivery_status: token?.delivery_status || null,
        failed: token?.failed || false,
        to_delete: false, // We only show active participants
        deleted_on: null,
        email_status: employee?.email_status,
        email_status_text: employee?.email_status_text,
        updated_at: token?.updated_at || employee?.updated_at,
        categories: categories?.category,
        debrief_response: auditEligibleList 
          ? auditEligibleList.find(ae => ae?.email === employee?.email)?.debrief_response || null
          : null,
      };
    };

    // Since the summary counts are authoritative, we need to build a participant list
    // that matches total_requests exactly. This means including all active participants
    // plus completed participants, regardless of token status.
    
    // 1. Add all employees who have active tokens (not marked for deletion)
    employeeToActiveToken.forEach((activeToken, employeeId) => {
      if (!processedEmployees.has(employeeId)) {
        const employee = get_employees.find(({ id }) => id === employeeId);
        if (employee) {
          const isCompleted = _compliance.includes(employeeId);
          
          const participant = createParticipant(employeeId, activeToken, employee, isCompleted);
          if (participant) {
            EmployeeList.push(participant);
            processedEmployees.add(employeeId);
          }
        }
      }
    });

    // 2. Add all completed employees from compliance (they count toward total even if token deleted)
    _compliance.forEach((employeeId) => {
      if (!processedEmployees.has(employeeId)) {
        const employee = get_employees.find(({ id }) => id === employeeId);
        if (employee) {
          // Find any token for metadata (even deleted ones)
          const anyToken = tokens.find(t => t.employee === employeeId);
          
          const participant = createParticipant(employeeId, anyToken, employee, true);
          if (participant) {
            EmployeeList.push(participant);
            processedEmployees.add(employeeId);
          }
        }
      }
    });

    // 3. If we still don't have enough participants to match total_requests,
    // add additional employees from request_list until we reach the target count
    const targetCount = surveyData?.rawData?.total_requests || 0;
    if (EmployeeList.length < targetCount && _request.length > 0) {
      _request.forEach((employeeId) => {
        if (EmployeeList.length >= targetCount) return; // Stop when we reach target
        
        if (!processedEmployees.has(employeeId)) {
          const employee = get_employees.find(({ id }) => id === employeeId);
          if (employee) {
            // Find any token for this employee (even deleted/cancelled ones)
            const anyToken = tokens.find(t => t.employee === employeeId);
            const isCompleted = _compliance.includes(employeeId);
            
            const participant = createParticipant(employeeId, anyToken, employee, isCompleted);
            if (participant) {
              EmployeeList.push(participant);
              processedEmployees.add(employeeId);
            }
          }
        }
      });
    }

    // Fallback: If no participants found and we have an organization group, use group members
    if (EmployeeList.length === 0 && 
        surveyData?.rawData?.organization_group &&
        get_organization_groups?.organization_group?.id === surveyData.rawData.organization_group &&
        get_organization_groups?.organization_group?.members) {

      const groupMembers = get_organization_groups.organization_group.members;

      groupMembers.forEach((member) => {
        if (member && typeof member === 'object' && !processedEmployees.has(member.id)) {
          const participant = {
            id: `group_${member.id}`,
            firstName: member.first_name || "N/A",
            lastName: member.last_name || "",
            email: member.email || "N/A",
            status: "pending", // Default status for not started surveys
            submittedAt: null,
            remindersSent: 0,
            employeeId: member.id,
            participantId: null,
            employee_status: member.status,
            delivery_status: null,
            failed: null,
            to_delete: null,
            deleted_on: null,
            email_status: member.email_status,
            email_status_text: member.email_status_text,
            updated_at: member.updated_at,
            categories: null,
            debrief_response: null,
          };
          EmployeeList.push(participant);
          processedEmployees.add(member.id);
        }
      });
    }

    // Sort by participant ID (safe numeric-or-string compare)
    const sortedEmployees = EmployeeList.sort((a, b) => {
      const aNum = Number(a?.id);
      const bNum = Number(b?.id);
      const aIsNum = Number.isFinite(aNum);
      const bIsNum = Number.isFinite(bNum);

      if (aIsNum && bIsNum) return aNum - bNum; // both numeric
      if (aIsNum) return -1; // numbers before strings
      if (bIsNum) return 1;

      const aStr = String(a?.id ?? "");
      const bStr = String(b?.id ?? "");
      return aStr.localeCompare(bStr);
    });

    setParticipants(sortedEmployees);
    setParticipantsLoading(false);
    // Keep table in loading state until tokens are ready to avoid status flicker
    setRecipientsTableLoading(!tokensReady);
  }, [
    debriefSchedule,
    get_employees,
    get_debrief_tokens,
    completedEmpId,
    auditEligibleList,
    get_employee_org_category,
    dispatch,
    surveyData,
    get_organization_groups?.organization_group?.id,
    get_organization_groups?.organization_group?.members,
    tokensReady,
  ]);

  // Use only real participants data - no mock data, excluding cancelled recipients entirely
  const allRecipients = useMemo(
    () => participants.filter((participant) => participant && participant.status !== "cancelled"),
    [participants]
  );

  // Filter and sort recipients based on search term, status filter, and sorting
  const filteredAndSortedRecipients = useMemo(() => {
    let filtered = allRecipients;

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (recipient) => recipient.status === statusFilter
      );
    }

    // Filter out any invalid recipients (but allow string IDs like "group_123")
    filtered = filtered?.filter((recipient) => recipient?.id && recipient?.employeeId);

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (recipient) =>
          recipient.firstName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          recipient.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          recipient.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case "name":
          aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
          bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
          break;
        case "email":
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case "status":
          aValue = a.status;
          bValue = b.status;
          break;
        case "submitted":
          aValue = a.submittedAt || "";
          bValue = b.submittedAt || "";
          break;
        case "reminders":
          aValue = a.remindersSent;
          bValue = b.remindersSent;
          break;
        default:
          aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
          bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [allRecipients, searchTerm, statusFilter, sortField, sortDirection]);

  // Pagination logic
  const totalPages = Math.ceil(
    filteredAndSortedRecipients.length / recipientsPerPage
  );
  const currentRecipients = filteredAndSortedRecipients.slice(
    (currentPage - 1) * recipientsPerPage,
    currentPage * recipientsPerPage
  );

  // Sync page input value with current page when it changes externally
  useEffect(() => {
    setPageInputValue(currentPage.toString());
  }, [currentPage]);

  // Handle recipient removal - show confirmation modal
  const handleRemoveRecipient = (recipientId) => {
    if (isSurveyClosed) {
      return;
    }

    const recipient = allRecipients.find((r) => r.id === recipientId);
    setRecipientToDelete(recipient);
    setShowDeleteModal(true);
  };

  // Close row menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeRowMenu && !event.target.closest('[data-row-menu]')) {
        setActiveRowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeRowMenu]);

  useEffect(() => {
    if (!showOptionsMenu) {
      return undefined;
    }

    const handleClickAway = (event) => {
      const target = event.target;
      if (
        (optionsButtonRef.current && optionsButtonRef.current.contains(target)) ||
        (optionsMenuRef.current && optionsMenuRef.current.contains(target))
      ) {
        return;
      }

      setShowOptionsMenu(false);
      setOptionsAnchor(null);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        setShowOptionsMenu(false);
        setOptionsAnchor(null);
      }
    };

    document.addEventListener('mousedown', handleClickAway);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickAway);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showOptionsMenu]);

  // Confirm recipient removal
  const confirmRemoveRecipient = () => {
    if (isSurveyClosed) {
      return;
    }

    if (recipientToDelete && recipientToDelete.employeeId) {
      // 1) Find the token for this employee on this survey
      const token = Array.isArray(get_debrief_tokens)
        ? get_debrief_tokens.find((t) => t?.employee === recipientToDelete.employeeId)
        : null;

      // 2) If a token exists, PATCH it via CloseSurvey (SG_EDIT_DEBRIEF_TOKEN)
      if (token?.id) {
        dispatch({
          type: SG_EDIT_DEBRIEF_TOKEN,
          payload: {
            id: token.id,
            close_survey: token.id,
          },
        });
      } else {
        // No token yet (e.g., only in request_list). We’ll still optimistically remove locally
        // and rely on the subsequent refresh to reflect server state or other handlers.
        addToast("No token found for this recipient; removing locally and refreshing…", {
          appearance: "warning",
        });
      }

      // 3) Optimistically remove from the local table for immediate UX
      setParticipants((prev) => prev.filter((p) => p.id !== recipientToDelete.id));

      addToast(
        `${recipientToDelete.firstName} ${recipientToDelete.lastName} removed from survey`,
        { appearance: "success" }
      );
      setShowDeleteModal(false);
      setRecipientToDelete(null);

      // 4) Refresh schedule + tokens to reconcile authoritative state
      setTimeout(() => {
        if (id) {
          setRecipientsTableLoading(true);
          dispatch({ type: SG_FETCH_DEBRIEF_SCHEDULE, payload: { id } });
          dispatch({ type: SG_GET_DEBRIEF_TOKENS, payload: `debrief_schedule=${id}` });
        }
      }, 800);
    }
  };

  // Cancel recipient removal
  const cancelRemoveRecipient = () => {
    setShowDeleteModal(false);
    setRecipientToDelete(null);
  };

  // Handle adding new recipients (bulk)
  const handleAddRecipients = (newRecipientIds) => {
    if (isSurveyClosed) {
      return;
    }

    if (newRecipientIds && newRecipientIds.length > 0) {
      // For live surveys, use the bulk_update_members endpoint 
      // This will add to group AND create tokens AND send emails
      const organizationGroupId = surveyData?.rawData?.organization_group;
      
      if (organizationGroupId) {
        dispatch({
          type: SG_BULK_UPDATE_ORGANIZATION_GROUP,
          payload: {
            organization_group_id: organizationGroupId,
            employee_ids: newRecipientIds,
            action: "add",
            create_tokens: true,  // Create tokens since this is a live survey
            send_email: true,     // Send emails to new recipients
          },
        });

        addToast(`Adding ${newRecipientIds.length} recipient${newRecipientIds.length !== 1 ? 's' : ''} to the group and sending survey invitations...`, {
          appearance: "success",
        });
      } else {
        // Fallback to the old method if no organization group found
        dispatch({
          type: SG_ADD_DEBRIEF_SCHEDULE_ADD_USERS_BULK,
          payload: {
            debrief_schedule: id,
            employee_ids: newRecipientIds,
          },
        });

        addToast(`Adding ${newRecipientIds.length} recipient${newRecipientIds.length !== 1 ? 's' : ''} and creating tokens...`, {
          appearance: "success",
        });
      }

      // Refresh participants data after adding by re-fetching the schedule
      setTimeout(() => {
        if (id) {
          setRecipientsTableLoading(true);
          dispatch({
            type: SG_FETCH_DEBRIEF_SCHEDULE,
            payload: { id: id },
          });
          dispatch({
            type: SG_GET_DEBRIEF_TOKENS,
            payload: `debrief_schedule=${id}`,
          });
        }
      }, 1500); // Slightly longer timeout for group update + token creation
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Handle status filter change
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle page jump input
  const handlePageJump = (e) => {
    if (e.key === "Enter") {
      const pageNumber = parseInt(pageInputValue, 10);
      if (
        !isNaN(pageNumber) &&
        pageNumber >= 1 &&
        pageNumber <= totalPages
      ) {
        setCurrentPage(pageNumber);
      } else {
        // Reset to current page if invalid
        setPageInputValue(currentPage.toString());
      }
    }
  };

  // Handle blur - reset to current page if invalid
  const handlePageInputBlur = () => {
    const pageNumber = parseInt(pageInputValue, 10);
    if (
      isNaN(pageNumber) ||
      pageNumber < 1 ||
      pageNumber > totalPages
    ) {
      setPageInputValue(currentPage.toString());
    }
  };

  // Handle extend deadline form
  const handleExtendDeadlineChange = (field, value) => {
    setExtendDeadlineData((prev) => ({
      ...prev,
      [field]: value,
      // Clear quick option if manual date is selected
      ...(field === "newEndDate" || field === "newEndTime"
        ? { quickOption: "" }
        : {}),
    }));
  };

  const handleQuickExtend = (option) => {
    const currentEndDate = new Date(surveyData.endDate);
    let newDate;

    switch (option) {
      case "1week":
        newDate = new Date(currentEndDate.getTime() + 7 * 24 * 60 * 60 * 1000);
        break;
      case "2weeks":
        newDate = new Date(currentEndDate.getTime() + 14 * 24 * 60 * 60 * 1000);
        break;
      case "1month":
        newDate = new Date(currentEndDate);
        newDate.setMonth(newDate.getMonth() + 1);
        break;
      default:
        return;
    }
    // Format as local date string (YYYY-MM-DD)
    const y = newDate.getFullYear();
    const m = String(newDate.getMonth() + 1).padStart(2, "0");
    const d = String(newDate.getDate()).padStart(2, "0");
    setExtendDeadlineData({
      newEndDate: `${y}-${m}-${d}`,
      newEndTime: surveyData.endTime,
      quickOption: option,
    });
  };

  const submitExtendDeadline = () => {
    if (!extendDeadlineData.newEndDate || !extendDeadlineData.newEndTime) {
      addToast("Please select a new end date and time", {
        appearance: "error",
      });
      return;
    }

    // Helper: build a Date in local time from separate date (YYYY-MM-DD) and time (HH:mm)
    const toLocalDateTime = (dateStr, timeStr) => {
      if (!dateStr || typeof dateStr !== "string") return null;
      const [y, mo, d] = dateStr.split("-").map((n) => parseInt(n, 10));
      if (!y || !mo || !d) return null;
      let h = 0, mi = 0;
      if (typeof timeStr === "string" && timeStr.includes(":")) {
        const [hh, mm] = timeStr.split(":");
        h = parseInt(hh, 10);
        mi = parseInt(mm, 10) || 0;
      }
      // Construct using local components to avoid UTC interpretation of YYYY-MM-DD
      return new Date(y, mo - 1, d, Number.isFinite(h) ? h : 0, Number.isFinite(mi) ? mi : 0, 0, 0);
    };

    // Build the proposed new end datetime (local)
    const newEndDateTime = toLocalDateTime(
      extendDeadlineData.newEndDate,
      extendDeadlineData.newEndTime
    );

    // Current end comes as a full ISO timestamp already (e.g., "2025-08-27T13:56:00Z")
    const currentEndDateTime = surveyData?.endDate
      ? new Date(surveyData.endDate)
      : null;

    if (!newEndDateTime || Number.isNaN(newEndDateTime.getTime())) {
      addToast("Invalid new end date/time", { appearance: "error" });
      return;
    }
    if (!currentEndDateTime || Number.isNaN(currentEndDateTime.getTime())) {
      addToast("Invalid current end date/time on survey", { appearance: "error" });
      return;
    }

    if (newEndDateTime <= currentEndDateTime) {
      addToast("New deadline must be after the current deadline", {
        appearance: "error",
      });
      return;
    }

    // Calculate minutes difference for the API
    const minutesDifference = Math.floor(
      (newEndDateTime.getTime() - currentEndDateTime.getTime()) / (1000 * 60)
    );

    if (id && minutesDifference > 0) {
      dispatch({
        type: SG_EXTEND_SURVEY_TIME,
        payload: {
          id: id,
          extend_minutes: minutesDifference,
        },
      });
      // Optimistically update local detailed schedule so UI reflects immediately
      setDebriefSchedule((prev) => ({
        ...(prev || {}),
        close_at: newEndDateTime.toISOString(),
      }));

      addToast("Survey deadline extended successfully", {
        appearance: "success",
      });
      setShowExtendDeadlineModal(false);
      setExtendDeadlineData({ newEndDate: "", newEndTime: "", quickOption: "" });
    } else {
      addToast("Invalid extension time", {
        appearance: "error",
      });
    }
  };
  // When survey extension is recorded in reducer, refetch the detailed schedule for authoritative values
  useEffect(() => {
    if (get_debrief_schedules?.survey_extended && id) {
      dispatch({ type: SG_FETCH_DEBRIEF_SCHEDULE, payload: { id } });
    }
  }, [dispatch, id, get_debrief_schedules?.survey_extended]);

  // Handle reschedule form
  const handleRescheduleChange = (field, value) => {
    setRescheduleData((prev) => ({
      ...prev,
      [field]: value,
      // Clear quick option if manual date is selected
      ...(field === "newStartDate" || field === "newStartTime"
        ? { quickOption: "" }
        : {}),
    }));
  };

  const handleQuickReschedule = (option) => {
    const currentStartDate = new Date(surveyData.startDate);
    let newDate;

    switch (option) {
      case "1hour":
        newDate = new Date(currentStartDate.getTime() + 1 * 60 * 60 * 1000);
        break;
      case "1day":
        newDate = new Date(currentStartDate.getTime() + 1 * 24 * 60 * 60 * 1000);
        break;
      case "3days":
        newDate = new Date(currentStartDate.getTime() + 3 * 24 * 60 * 60 * 1000);
        break;
      default:
        return;
    }
    // Format as local date string (YYYY-MM-DD)
    const y = newDate.getFullYear();
    const m = String(newDate.getMonth() + 1).padStart(2, "0");
    const d = String(newDate.getDate()).padStart(2, "0");
    setRescheduleData({
      newStartDate: `${y}-${m}-${d}`,
      newStartTime: surveyData.startTime,
      quickOption: option,
    });
  };

  const submitReschedule = () => {
    if (!rescheduleData.newStartDate || !rescheduleData.newStartTime) {
      addToast("Please select a new start date and time", {
        appearance: "error",
      });
      return;
    }

    // Helper: build a Date in local time from separate date (YYYY-MM-DD) and time (HH:mm)
    const toLocalDateTime = (dateStr, timeStr) => {
      if (!dateStr || typeof dateStr !== "string") return null;
      const [y, mo, d] = dateStr.split("-").map((n) => parseInt(n, 10));
      if (!y || !mo || !d) return null;
      let h = 0, mi = 0;
      if (typeof timeStr === "string" && timeStr.includes(":")) {
        const [hh, mm] = timeStr.split(":");
        h = parseInt(hh, 10);
        mi = parseInt(mm, 10) || 0;
      }
      // Construct using local components to avoid UTC interpretation of YYYY-MM-DD
      return new Date(y, mo - 1, d, Number.isFinite(h) ? h : 0, Number.isFinite(mi) ? mi : 0, 0, 0);
    };

    // Build the proposed new start datetime (local)
    const newStartDateTime = toLocalDateTime(
      rescheduleData.newStartDate,
      rescheduleData.newStartTime
    );

    // Current start comes as a full ISO timestamp already
    const currentStartDateTime = surveyData?.startDate
      ? new Date(surveyData.startDate)
      : null;

    if (!newStartDateTime || Number.isNaN(newStartDateTime.getTime())) {
      addToast("Invalid new start date/time", { appearance: "error" });
      return;
    }
    if (!currentStartDateTime || Number.isNaN(currentStartDateTime.getTime())) {
      addToast("Invalid current start date/time on survey", { appearance: "error" });
      return;
    }

    // For rescheduling, we can move to an earlier or later date
    const now = new Date();
    if (newStartDateTime < now) {
      addToast("New start date must be in the future", {
        appearance: "error",
      });
      return;
    }

    if (id) {
      dispatch({
        type: SG_EDIT_DEBRIEF_SCHEDULE,
        payload: {
          id: Number(id),
          scheduled_for: newStartDateTime.toISOString(),
        },
      });

      // Optimistically update local detailed schedule so UI reflects immediately
      setDebriefSchedule((prev) => ({
        ...(prev || {}),
        scheduled_for: newStartDateTime.toISOString(),
      }));

      addToast("Survey rescheduled successfully", {
        appearance: "success",
      });
      setShowRescheduleModal(false);
      setRescheduleData({ newStartDate: "", newStartTime: "", quickOption: "" });
    } else {
      addToast("Invalid survey ID", {
        appearance: "error",
      });
    }
  };

  // Handle custom reminder form
  const handleCustomReminderChange = (field, value) => {
    setCustomReminderData((prev) => ({ ...prev, [field]: value }));
  };

  const submitCustomReminder = () => {
    if (isSurveyClosed) {
      return;
    }

    // Strip HTML tags and check if the body has any non-whitespace content
    const bodyTextContent = (customReminderData.body || "").replace(/<[^>]*>/g, "").trim();
    if (!customReminderData.subject.trim() || !bodyTextContent) {
      addToast("Please fill in both subject and body", { appearance: "error" });
      return;
    }

    if (
      !customReminderData.sendNow &&
      (!customReminderData.scheduledDate || !customReminderData.scheduledTime)
    ) {
      addToast(
        "Please select a scheduled date and time, or choose to send now",
        { appearance: "error" }
      );
      return;
    }

    // Create scheduled_for timestamp if not sending now
    let scheduledFor = null;
    if (!customReminderData.sendNow) {
      // Interprets the selected time in the user's local timezone before converting to UTC for the API
      const scheduledDateTime = new Date(`${customReminderData.scheduledDate}T${customReminderData.scheduledTime}`);
      scheduledFor = scheduledDateTime.toISOString();
    }

    // Dispatch the custom reminder email using the same logic as CustomEmail component
    dispatch({
      type: SG_ADD_DEBRIEF_EMAIL,
      payload: {
        status: 2,
        name: "custom email",
        content: {
          subject: customReminderData.subject,
          body: customReminderData.body,
        },
        debrief_schedule: id,
        sender: get_employee?.userEmp,
        ...(scheduledFor && { scheduled_for: scheduledFor }),
      },
    });

    const action = customReminderData.sendNow ? "sent" : "scheduled";
    addToast(`Custom reminder ${action} successfully. Please check after a couple of minutes.`, {
      appearance: "success",
    });
    setShowCustomReminderModal(false);
    setCustomReminderData({
      subject: "",
      body: "<p></p>",
      scheduledDate: "",
      scheduledTime: "",
      sendNow: false,
    });

    // Refresh participants data after sending custom reminder to update reminder counts
    setTimeout(() => {
      if (id) {
        setRecipientsTableLoading(true);
        dispatch({
          type: SG_FETCH_DEBRIEF_SCHEDULE,
          payload: { id: id },
        });
        dispatch({
          type: SG_GET_DEBRIEF_TOKENS,
          payload: `debrief_schedule=${id}`,
        });
      }
    }, 2000); // Wait 2 seconds for the email to be processed
  };

  // Handle cancel survey modal
  const openCancelSurveyModal = () => {
    if (isSurveyClosed) {
      return;
    }

    setShowCancelSurveyModal(true);
  };
  const closeCancelSurveyModal = () => setShowCancelSurveyModal(false);

  const confirmCancelSurvey = async () => {
    if (isSurveyClosed) {
      return;
    }

    try {
      setCancelBusy(true);
      // reuse prior DELETE pattern from PendingList_V2
      dispatch({
        type: SG_DELETE_DEBRIEF_SCHEDULE,
        payload: { id }, // schedule id
      });

      addToast("Survey cancelled successfully.", { appearance: "success" });

      // navigate back to list
      navigate("/leader/audit/schedule-v2");
    } catch (e) {
      addToast("Unable to cancel survey.", { appearance: "error" });
    } finally {
      setCancelBusy(false);
      closeCancelSurveyModal();
    }
  };

  // Handle send reminder confirmation (RemindAll functionality from SurveyStatusPending.js)
  const confirmSendReminder = () => {
    if (isSurveyClosed) {
      return;
    }

    if (id && !(timeLeft > 0)) {
      setTimeLeft(330); // 5 minute 30 second cooldown
      dispatch({
        type: SG_EDIT_DEBRIEF_SCHEDULE,
        payload: {
          id: id,
          remind_all: id,
        },
      });
      addToast(
        "Successfully set up a reminder, this can take about 5 minutes for emails to go",
        { appearance: "success" }
      );
    } else {
      addToast(
        "Please wait for about 5 minutes for previously queued action to be completed",
        { appearance: "warning" }
      );
    }
    setShowSendReminderModal(false);
  };

  // Handle delete custom email
  const handleDeleteCustomEmail = (emailId) => {
    if (isSurveyClosed) {
      return;
    }

    if (emailId) {
      dispatch({
        type: SG_DELETE_DEBRIEF_EMAIL,
        payload: { id: emailId },
      });
      addToast("Email deleted successfully", { appearance: "success" });
      // Refresh email data
      setTimeout(() => {
        if (id) {
          dispatch({
            type: SG_GET_DEBRIEF_EMAIL,
            payload: `debrief_schedule=${id}`,
          });
        }
      }, 1000);
    }
  };

  // Handle edit name change
  const handleEditNameChange = (field, value) => {
    setEditNameData((prev) => ({ ...prev, [field]: value }));
  };

  const submitEditName = () => {
    if (!editNameData.name.trim()) {
      addToast("Please enter a survey name", { appearance: "error" });
      return;
    }

    if (id) {
      dispatch({
        type: SG_EDIT_DEBRIEF_SCHEDULE,
        payload: {
          id: Number(id),
          name: editNameData.name.trim(),
        },
      });

      // Optimistically update local detailed schedule so UI reflects immediately
      setDebriefSchedule((prev) => ({
        ...(prev || {}),
        name: editNameData.name.trim(),
      }));

      addToast("Survey name updated successfully", {
        appearance: "success",
      });
      setShowEditNameModal(false);
      setEditNameData({ name: "" });

      // Refresh schedule data to get authoritative state from server
      setTimeout(() => {
        if (id) {
          dispatch({ type: SG_FETCH_DEBRIEF_SCHEDULE, payload: { id } });
        }
      }, 800);
    } else {
      addToast("Invalid survey ID", {
        appearance: "error",
      });
    }
  };

  // Handle options menu actions
  const handleOptionClick = (action) => {
    setShowOptionsMenu(false);
    setOptionsAnchor(null);

    if (action === "csv") {
      setShowCsvModal(true);
      return;
    }

    if (isSurveyClosed) {
      return;
    }

    switch (action) {
      case "editName":
        // Pre-populate with current name
        setEditNameData({ name: surveyData?.name || "" });
        setShowEditNameModal(true);
        break;
      case "compliance":
        setShowComplianceModal(true);
        break;
      case "extend":
        setShowExtendDeadlineModal(true);
        break;
      case "reschedule":
        setShowRescheduleModal(true);
        break;
      case "download":
        // Download email list of participants
        if (allRecipients.length > 0) {
          const csvContent = [
            ["Name", "Email", "Status"],
            ...allRecipients.map((r) => [
              `${r.firstName} ${r.lastName}`,
              r.email,
              r.status,
            ]),
          ]
            .map((row) => row.join(","))
            .join("\n");

          const blob = new Blob([csvContent], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${surveyData.title}_recipients.csv`;
          a.click();
          window.URL.revokeObjectURL(url);

          addToast("Email list downloaded", { appearance: "success" });
        } else {
          addToast("No recipients to download", { appearance: "warning" });
        }
        break;
      case "reminder":
        setShowCustomReminderModal(true);
        break;
      case "send":
        setShowSendReminderModal(true);
        break;
      case "status":
        // Open email status modal
        setShowEmailStatusModal(true);
        break;
      case "publicLink":
        // Open public link modal
        setShowPublicLink(true);
        break;
      case "close":
        // Close survey using the same dispatch logic as SurveyStatusPending.js
        if (id) {
          dispatch({
            type: SG_EDIT_DEBRIEF_SCHEDULE,
            payload: {
              id: id,
              close_survey: id,
            },
          });
          addToast("Survey closed successfully. This can take about 5 minutes.", { 
            appearance: "warning" 
          });
          // Navigate back to surveys list after closing
          setTimeout(() => {
            navigate("/leader/audit/schedule-v2");
          }, 2000);
        } else {
          addToast("There is no survey ID", { appearance: "error" });
        }
        break;
      default:
        break;
    }
  };

  const handleOpenComplianceReport = () => {
    setShowOptionsMenu(false);
    setOptionsAnchor(null);
    setShowComplianceModal(true);
  };

  const handleNavigateToQuestionCompliance = () => {
    setShowOptionsMenu(false);
    setOptionsAnchor(null);

    if (surveyData?.id) {
      navigate(`/leader/audit/simple/${surveyData.id}/question-compliance`);
      return;
    }

    navigate("/leader/audit/simple/question-compliance");
  };


  // Format date/time
  const formatDateTime = (date, time) => {
    if (!date) return "N/A";
    const dt = new Date(date);
    if (time && typeof time === 'string') {
      const [h, m] = time.split(":").map((n) => parseInt(n, 10));
      if (!Number.isNaN(h)) {
        dt.setHours(h, Number.isNaN(m) ? 0 : m, 0, 0);
      }
    }
    return `${dt.toLocaleDateString()} at ${dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`;
  };

  // Format completion status based on participant status
  const getStatusBadge = (participantStatus) => {
    switch (participantStatus) {
      case "completed":
        return (
          <StatusBadge status="completed">
            <CheckCircleIcon /> Completed
          </StatusBadge>
        );
      case "in_progress":
        return (
          <StatusBadge status="in_progress">
            <ClockIcon /> In Progress
          </StatusBadge>
        );
      case "incomplete":
        return (
          <StatusBadge status="incomplete">
            <ClockIcon /> Incomplete
          </StatusBadge>
        );
      case "email_bounced":
        return (
          <StatusBadge status="email_bounced">
            <WarningIcon /> Email Bounced
          </StatusBadge>
        );
      case "pending":
      default:
        return (
          <StatusBadge status="pending">
            <ClockIcon /> Pending
          </StatusBadge>
        );
    }
  };

  // Show loading or error if survey not found
  if (!surveyData) {
    return (
      <Container>
        <ScheduleBreadcrumb crumbs={breadcrumbCrumbs} />
        {recipientsTableLoading ? (
          <LoadingState data-testid="recipients-loading">
            <LoadingSpinner />
            <LoadingText>Loading survey...</LoadingText>
          </LoadingState>
        ) : (
          <ErrorState>
            <ErrorTitle>Survey Not Found</ErrorTitle>
            <ErrorText>
              The survey you're looking for could not be found.
            </ErrorText>
          </ErrorState>
        )}
      </Container>
    );
  }


  return (
    <Container
      data-testid="manage-active-survey"
      data-survey-id={surveyData?.id}
    >
      <ScheduleBreadcrumb crumbs={breadcrumbCrumbs} />
      {/* Header */}
      <HeaderSection>
        <HeaderContent>
          <IconWrapper>
            <CalendarIcon />
          </IconWrapper>
          <HeaderText>
            <Title>{headerTitle}</Title>
            <Subtitle>
              {surveyData.title} • {get_organizations?.[organizationId]?.name} •{" "}
              {isHydratingRecipients
                ? "Loading completion rate..."
                : `${tokenDerivedCounts.completionRate}% completion rate`}
            </Subtitle>
          </HeaderText>
        </HeaderContent>

        {!isSurveyClosed && (
          <HeaderActions>
            <ActionButton
              data-testid="add-recipients-open"
              onClick={() => setShowAddModal(true)}
            >
              <UserPlusIcon />
              <span>Add Recipients</span>
            </ActionButton>

            <OptionsMenuWrapper>
              <OptionsButton
                ref={optionsButtonRef}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  if (showOptionsMenu) {
                    setShowOptionsMenu(false);
                    setOptionsAnchor(null);
                  } else {
                    setOptionsAnchor(rect);
                    setShowOptionsMenu(true);
                  }
                }}
              >
                <SettingsIcon />
                <span>Options</span>
                <MoreVertIcon />
              </OptionsButton>

              {showOptionsMenu && optionsAnchor && (
                <PopoverPortal>
                  <FloatingMenu
                    ref={optionsMenuRef}
                    data-row-menu
                    style={{
                      position: "fixed",
                      top: optionsAnchor.bottom + 4,
                      left: optionsAnchor.right,
                      transform: "translateX(-100%)",
                    }}
                  >
                    <OptionsMenuItem
                      onClick={() => handleOptionClick("editName")}
                    >
                      Change survey name
                    </OptionsMenuItem>
                    <OptionsMenuDivider />
                    <OptionsMenuItem
                      onClick={() => handleOptionClick("compliance")}
                    >
                      Participation by category
                    </OptionsMenuItem>
                    <OptionsMenuItem onClick={() => handleOptionClick("extend")}>
                      Extend survey deadline
                    </OptionsMenuItem>
                    {/* 
                     //! DOES NOT APPEAR TO WORK, NEVER EXISTED AS A FEATURE
                    {!hasStarted && (
                      <OptionsMenuItem onClick={() => handleOptionClick("reschedule")}>
                        Reschedule start time
                      </OptionsMenuItem>
                    )} */}
                    <OptionsMenuItem
                      onClick={() => handleOptionClick("download")}
                    >
                      Download email list
                    </OptionsMenuItem>
                    {showCsvHack && (
                      <OptionsMenuItem onClick={() => handleOptionClick("csv")}>
                        Generate CSV Report
                      </OptionsMenuItem>
                    )}
                    <OptionsMenuItem
                      onClick={() => handleOptionClick("reminder")}
                    >
                      Send a custom message
                    </OptionsMenuItem>
                    <OptionsMenuItem onClick={() => handleOptionClick("send")}>
                      Send reminder
                    </OptionsMenuItem>
                    <OptionsMenuItem onClick={() => handleOptionClick("status")}>
                      Email reminder status
                    </OptionsMenuItem>
                    {surveyData?.rawData?.include_members && (
                      <OptionsMenuItem onClick={() => handleOptionClick("publicLink")}>
                        View public link
                      </OptionsMenuItem>
                    )}
                    <OptionsMenuDivider />
                    <OptionsMenuItem
                      onClick={() => handleOptionClick("close")}
                      danger
                    >
                      Close the survey
                    </OptionsMenuItem>
                  </FloatingMenu>
                </PopoverPortal>
              )}
            </OptionsMenuWrapper>
          </HeaderActions>
        )}
        {isSurveyClosed && (
          <HeaderActions>
            <OptionsMenuWrapper>
              <OptionsButton
                ref={optionsButtonRef}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  if (showOptionsMenu) {
                    setShowOptionsMenu(false);
                    setOptionsAnchor(null);
                  } else {
                    setOptionsAnchor(rect);
                    setShowOptionsMenu(true);
                  }
                }}
              >
                <SettingsIcon />
                <span>Options</span>
                <MoreVertIcon />
              </OptionsButton>

              {showOptionsMenu && optionsAnchor && (
                <PopoverPortal>
                  <FloatingMenu
                    ref={optionsMenuRef}
                    data-row-menu
                    style={{
                      position: "fixed",
                      top: optionsAnchor.bottom + 4,
                      left: optionsAnchor.right,
                      transform: "translateX(-100%)",
                    }}
                  >
                    <OptionsMenuItem onClick={handleOpenComplianceReport}>
                      Participation by category
                    </OptionsMenuItem>
                    <OptionsMenuItem onClick={handleNavigateToQuestionCompliance}>
                      Participation by question
                    </OptionsMenuItem>
                    {showCsvHack && (
                      <OptionsMenuItem onClick={() => handleOptionClick("csv")}>
                        Generate CSV Report
                      </OptionsMenuItem>
                    )}
                  </FloatingMenu>
                </PopoverPortal>
              )}
            </OptionsMenuWrapper>
          </HeaderActions>
        )}
      </HeaderSection>

      {/* Not Started Banner */}
      {!hasStarted && !isSurveyClosed && (
        <InfoBanner role="status" aria-live="polite">
          <BannerTitle>Survey Not Started</BannerTitle>
          <BannerText>
            This survey is scheduled to start on{" "}
            <strong>
              {formatDateTime(surveyData.startDate, surveyData.startTime)}
            </strong>
            . You can cancel it before it begins.
          </BannerText>
          <BannerActions>
            <DangerButton onClick={openCancelSurveyModal}>
              Cancel Survey
            </DangerButton>
          </BannerActions>
        </InfoBanner>
      )}

      {/* Survey Details */}
      <SurveyDetailsSection>
        <SurveyDetailsCard>
          <CardHeader>
            <CardTitle>Survey Schedule</CardTitle>
          </CardHeader>
          <CardContent>
            <ScheduleGrid>
              <ScheduleItem>
                <ScheduleLabel>Start Date & Time</ScheduleLabel>
                <ScheduleValue>
                  {formatDateTime(surveyData.startDate, surveyData.startTime)}
                </ScheduleValue>
              </ScheduleItem>
              <ScheduleItem>
                <ScheduleLabel>End Date & Time</ScheduleLabel>
                <ScheduleValue>
                  {formatDateTime(surveyData.endDate, surveyData.endTime)}
                </ScheduleValue>
              </ScheduleItem>
              <ScheduleItem>
                <ScheduleLabel>Timezone</ScheduleLabel>
                <ScheduleValue title={surveyData.timezone}>
                  {displayTimezone || surveyData.timezone}
                </ScheduleValue>
              </ScheduleItem>
              <ScheduleItem>
                <ScheduleLabel>Survey Type</ScheduleLabel>
                <ScheduleValue>
                  <SurveyTypeBadge>{surveyData.type}</SurveyTypeBadge>
                </ScheduleValue>
              </ScheduleItem>
            </ScheduleGrid>
          </CardContent>
        </SurveyDetailsCard>

        <StatsCards>
          {isHydratingRecipients ? (
            <StatsLoadingCard role="status" aria-live="polite">
              <TableLoadingSpinner />
              <TableLoadingText>Loading stats...</TableLoadingText>
            </StatsLoadingCard>
          ) : (
            <>
              <StatCard>
                <StatValue>{tokenDerivedCounts.total}</StatValue>
                <StatLabel>Total Recipients</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{tokenDerivedCounts.completed}</StatValue>
                <StatLabel>Completed</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{tokenDerivedCounts.pending}</StatValue>
                <StatLabel>Pending</StatLabel>
              </StatCard>
            </>
          )}
        </StatsCards>
      </SurveyDetailsSection>

      {/* Recipients Table */}
      <RecipientsSection>
        <TableHeader>
          <TableHeaderLeft>
            <TableTitle data-testid="recipients-count">
              Survey Recipients (
              {recipientsTableLoading ? "..." : filteredAndSortedRecipients.length}
              )
            </TableTitle>
          </TableHeaderLeft>
          <TableHeaderRight>
            <TableHeaderActions>
              {/* Status Filter */}
              <StatusFilterContainer>
                <FilterIcon />
                <StatusFilterSelect
                  data-testid="recipients-status-filter"
                  value={statusFilter}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="in_progress">In Progress</option>
                  <option value="pending">Pending</option>
                  <option value="email_bounced">Email Bounced</option>
                </StatusFilterSelect>
              </StatusFilterContainer>

              {/* Search */}
              <SearchContainer>
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
                <SearchInput
                  data-testid="recipients-search"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search recipients..."
                />
              </SearchContainer>
            </TableHeaderActions>
          </TableHeaderRight>
        </TableHeader>

        <TableContainer data-testid="recipients-table-container">
          {recipientsTableLoading && (
            <TableLoadingOverlay role="status" aria-live="polite">
              <TableLoadingSpinner />
              <TableLoadingText>Loading recipients...</TableLoadingText>
            </TableLoadingOverlay>
          )}
          <RecipientsTable data-testid="recipients-table" loading={recipientsTableLoading}>
            <TableHead>
              <TableHeadRow>
                <SortableTableHeadCell
                  onClick={() => handleSort("name")}
                  active={sortField === "name"}
                  direction={sortDirection}
                >
                  Name
                  <SortIcon />
                </SortableTableHeadCell>
                <SortableTableHeadCell
                  onClick={() => handleSort("email")}
                  active={sortField === "email"}
                  direction={sortDirection}
                >
                  Email
                  <SortIcon />
                </SortableTableHeadCell>
                <SortableTableHeadCell
                  onClick={() => handleSort("status")}
                  active={sortField === "status"}
                  direction={sortDirection}
                >
                  Status
                  <SortIcon />
                </SortableTableHeadCell>
                <SortableTableHeadCell
                  onClick={() => handleSort("reminders")}
                  active={sortField === "reminders"}
                  direction={sortDirection}
                >
                  Reminders Sent
                  <SortIcon />
                </SortableTableHeadCell>
                <TableHeadCell>Actions</TableHeadCell>
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {!recipientsTableLoading &&
                currentRecipients?.map((recipient, index) => (
                <TableRow
                  key={recipient.id}
                  index={index}
                  data-testid="recipient-row"
                  data-employee-id={recipient.employeeId}
                  data-status={recipient.status}
                >
                  <TableCell>
                    <RecipientName>
                      {recipient.firstName} {recipient.lastName}
                    </RecipientName>
                  </TableCell>
                  <TableCell>
                    <RecipientEmail>{recipient.email}</RecipientEmail>
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(recipient.status)}
                  </TableCell>
                  <TableCell>
                    <ReminderCount>{recipient.remindersSent}</ReminderCount>
                  </TableCell>
                  <TableCell>
                    {!isSurveyClosed && recipient?.status !== "completed" && (
                      <OptionsMenuWrapper data-row-menu>
                        <ActionButtonSmall
                          onClick={(e) => {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            const plainRect = {
                              top: rect.top,
                              bottom: rect.bottom,
                              left: rect.left,
                              right: rect.right,
                              width: rect.width,
                              height: rect.height,
                            };

                            setActiveRowMenu(
                              activeRowMenu?.id === recipient.id
                                ? null
                                : {
                                    id: recipient.id,
                                    rect: plainRect,
                                    placement: determineRowMenuPlacement(plainRect),
                                  }
                            );
                          }}
                        >
                          <MoreVertIcon />
                        </ActionButtonSmall>

                        {activeRowMenu?.id === recipient.id && (
                          <PopoverPortal>
                            <FloatingMenu
                              data-row-menu
                              style={{
                                position: "fixed",
                                top:
                                  activeRowMenu.placement === 'up'
                                    ? Math.max(4, activeRowMenu.rect.top - 4)
                                    : activeRowMenu.rect.bottom + 4,
                                left: activeRowMenu.rect.right,
                                transform:
                                  activeRowMenu.placement === 'up'
                                    ? 'translate(-100%, -100%)'
                                    : 'translateX(-100%)',
                                maxHeight: 'calc(100vh - 24px)',
                                overflowY: 'auto',
                              }}
                            >
                              <OptionsMenuItem
                                onClick={() =>
                                  handleRowMenuClick("copy-link", recipient)
                                }
                              >
                                Copy Survey Link
                              </OptionsMenuItem>
                              <OptionsMenuItem
                                onClick={() =>
                                  handleRowMenuClick("remind", recipient)
                                }
                              >
                                Send a reminder
                              </OptionsMenuItem>
                              <OptionsMenuDivider />
                              <OptionsMenuItem
                                onClick={() =>
                                  handleRowMenuClick("remove", recipient)
                                }
                                danger
                              >
                                <DeleteIcon />
                                Remove User
                              </OptionsMenuItem>
                            </FloatingMenu>
                          </PopoverPortal>
                        )}
                      </OptionsMenuWrapper>
                    )}
                  </TableCell>
                </TableRow>
                ))}
            </TableBody>
          </RecipientsTable>

          {!recipientsTableLoading && currentRecipients.length === 0 && (
            <EmptyState data-testid="recipients-empty">
              <EmptyIcon>👥</EmptyIcon>
              <EmptyTitle>No recipients found</EmptyTitle>
              <EmptyText>
                {searchTerm || statusFilter !== "all"
                  ? "Try adjusting your search or filter criteria"
                  : "Loading failed. Refresh the page or try again later."}
              </EmptyText>
            </EmptyState>
          )}
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <PaginationContainer>
            <PaginationButton
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </PaginationButton>

            <PaginationInfo>
              Page{" "}
              <PageJumpInput
                type="number"
                min="1"
                max={totalPages}
                value={pageInputValue}
                onChange={(e) => setPageInputValue(e.target.value)}
                onKeyDown={handlePageJump}
                onBlur={handlePageInputBlur}
              />{" "}
              of {totalPages}
            </PaginationInfo>

            <PaginationButton
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
            >
              Next
            </PaginationButton>
          </PaginationContainer>
        )}
      </RecipientsSection>

      {/* Add Recipients Modal */}
      {!isSurveyClosed && (
        <AddRecipientsModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          existingRecipients={allRecipients.map((r) => r.employeeId)}
          onAddRecipients={handleAddRecipients}
        />
      )}

      {/* Confirmation Modal */}
      {!isSurveyClosed && showDeleteModal && (
        <ConfirmationModalOverlay onClick={cancelRemoveRecipient}>
          <ConfirmationModalContainer onClick={(e) => e.stopPropagation()}>
            <ConfirmationModalHeader>
              <ConfirmationModalTitle>Remove Recipient</ConfirmationModalTitle>
            </ConfirmationModalHeader>

            <ConfirmationModalContent>
              <ConfirmationModalText>
                Are you sure you want to remove{" "}
                <strong>
                  {recipientToDelete?.firstName} {recipientToDelete?.lastName}
                </strong>{" "}
                from this survey?
              </ConfirmationModalText>
              <ConfirmationModalSubtext>
                This action cannot be undone. The recipient will no longer be
                able to access the survey.
              </ConfirmationModalSubtext>
            </ConfirmationModalContent>

            <ConfirmationModalFooter>
              <ConfirmationCancelButton onClick={cancelRemoveRecipient}>
                Cancel
              </ConfirmationCancelButton>
              <ConfirmationDeleteButton onClick={confirmRemoveRecipient}>
                Remove Recipient
              </ConfirmationDeleteButton>
            </ConfirmationModalFooter>
          </ConfirmationModalContainer>
        </ConfirmationModalOverlay>
      )}

      {/* Extend Deadline Modal */}
      {!isSurveyClosed && showExtendDeadlineModal && (
        <ConfirmationModalOverlay
          onClick={() => setShowExtendDeadlineModal(false)}
        >
          <ConfirmationModalContainer onClick={(e) => e.stopPropagation()}>
            <ConfirmationModalHeader>
              <ConfirmationModalTitle>
                Extend Survey Deadline
              </ConfirmationModalTitle>
              <CloseButton onClick={() => setShowExtendDeadlineModal(false)}>
                ×
              </CloseButton>
            </ConfirmationModalHeader>

            <ConfirmationModalContent>
              <ConfirmationModalText>
                Current deadline:{" "}
                <strong>
                  {formatDateTime(surveyData.endDate, surveyData.endTime)}
                </strong>
              </ConfirmationModalText>

              <QuickExtendSection>
                <QuickExtendTitle>Quick Options</QuickExtendTitle>
                <QuickExtendButtons>
                  <QuickExtendButton
                    active={extendDeadlineData.quickOption === "1week"}
                    onClick={() => handleQuickExtend("1week")}
                  >
                    Extend by 1 Week
                  </QuickExtendButton>
                  <QuickExtendButton
                    active={extendDeadlineData.quickOption === "2weeks"}
                    onClick={() => handleQuickExtend("2weeks")}
                  >
                    Extend by 2 Weeks
                  </QuickExtendButton>
                  <QuickExtendButton
                    active={extendDeadlineData.quickOption === "1month"}
                    onClick={() => handleQuickExtend("1month")}
                  >
                    Extend by 1 Month
                  </QuickExtendButton>
                </QuickExtendButtons>
              </QuickExtendSection>

              <DateTimeSection>
                <DateTimeTitle>Or Choose Custom Date</DateTimeTitle>
                <DateTimeInputs>
                  <DateTimeGroup>
                    <DateTimeLabel>New End Date</DateTimeLabel>
                    <DateTimeInput
                      type="date"
                      value={extendDeadlineData.newEndDate}
                      onChange={(e) =>
                        handleExtendDeadlineChange("newEndDate", e.target.value)
                      }
                      min={surveyData.endDate}
                    />
                  </DateTimeGroup>
                  <DateTimeGroup>
                    <DateTimeLabel>New End Time</DateTimeLabel>
                    <DateTimeInput
                      type="time"
                      value={extendDeadlineData.newEndTime}
                      onChange={(e) =>
                        handleExtendDeadlineChange("newEndTime", e.target.value)
                      }
                    />
                  </DateTimeGroup>
                </DateTimeInputs>
              </DateTimeSection>
            </ConfirmationModalContent>

            <ConfirmationModalFooter>
              <ConfirmationCancelButton
                onClick={() => setShowExtendDeadlineModal(false)}
              >
                Cancel
              </ConfirmationCancelButton>
              <ConfirmationDeleteButton onClick={submitExtendDeadline}>
                Extend Deadline
              </ConfirmationDeleteButton>
            </ConfirmationModalFooter>
          </ConfirmationModalContainer>
        </ConfirmationModalOverlay>
      )}

      {/* Reschedule Modal */}
      {!isSurveyClosed && showRescheduleModal && (
        <ConfirmationModalOverlay
          onClick={() => setShowRescheduleModal(false)}
        >
          <ConfirmationModalContainer onClick={(e) => e.stopPropagation()}>
            <ConfirmationModalHeader>
              <ConfirmationModalTitle>
                Reschedule Survey Start Time
              </ConfirmationModalTitle>
              <CloseButton onClick={() => setShowRescheduleModal(false)}>
                ×
              </CloseButton>
            </ConfirmationModalHeader>

            <ConfirmationModalContent>
              <ConfirmationModalText>
                Current start time:{" "}
                <strong>
                  {formatDateTime(surveyData.startDate, surveyData.startTime)}
                </strong>
              </ConfirmationModalText>

              <QuickExtendSection>
                <QuickExtendTitle>Quick Options</QuickExtendTitle>
                <QuickExtendButtons>
                  <QuickExtendButton
                    active={rescheduleData.quickOption === "1hour"}
                    onClick={() => handleQuickReschedule("1hour")}
                  >
                    Delay by 1 Hour
                  </QuickExtendButton>
                  <QuickExtendButton
                    active={rescheduleData.quickOption === "1day"}
                    onClick={() => handleQuickReschedule("1day")}
                  >
                    Delay by 1 Day
                  </QuickExtendButton>
                  <QuickExtendButton
                    active={rescheduleData.quickOption === "3days"}
                    onClick={() => handleQuickReschedule("3days")}
                  >
                    Delay by 3 Days
                  </QuickExtendButton>
                </QuickExtendButtons>
              </QuickExtendSection>

              <DateTimeSection>
                <DateTimeTitle>Or Choose Custom Date</DateTimeTitle>
                <DateTimeInputs>
                  <DateTimeGroup>
                    <DateTimeLabel>New Start Date</DateTimeLabel>
                    <DateTimeInput
                      type="date"
                      value={rescheduleData.newStartDate}
                      onChange={(e) =>
                        handleRescheduleChange("newStartDate", e.target.value)
                      }
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </DateTimeGroup>
                  <DateTimeGroup>
                    <DateTimeLabel>New Start Time</DateTimeLabel>
                    <DateTimeInput
                      type="time"
                      value={rescheduleData.newStartTime}
                      onChange={(e) =>
                        handleRescheduleChange("newStartTime", e.target.value)
                      }
                    />
                  </DateTimeGroup>
                </DateTimeInputs>
              </DateTimeSection>
            </ConfirmationModalContent>

            <ConfirmationModalFooter>
              <ConfirmationCancelButton
                onClick={() => setShowRescheduleModal(false)}
              >
                Cancel
              </ConfirmationCancelButton>
              <ConfirmationDeleteButton onClick={submitReschedule}>
                Reschedule Survey
              </ConfirmationDeleteButton>
            </ConfirmationModalFooter>
          </ConfirmationModalContainer>
        </ConfirmationModalOverlay>
      )}

      {/* Edit Name Modal */}
      {!isSurveyClosed && showEditNameModal && (
        <ConfirmationModalOverlay
          onClick={() => setShowEditNameModal(false)}
        >
          <ConfirmationModalContainer onClick={(e) => e.stopPropagation()}>
            <ConfirmationModalHeader>
              <ConfirmationModalTitle>
                Change Survey Name
              </ConfirmationModalTitle>
              <CloseButton onClick={() => setShowEditNameModal(false)}>
                ×
              </CloseButton>
            </ConfirmationModalHeader>

            <ConfirmationModalContent>
              <ConfirmationModalText>
                Current name:{" "}
                <strong>
                  {surveyData?.name || "Unnamed Survey"}
                </strong>
              </ConfirmationModalText>

              <FormGroup>
                <FormLabel>New Survey Name *</FormLabel>
                <FormInput
                  type="text"
                  value={editNameData.name}
                  onChange={(e) =>
                    handleEditNameChange("name", e.target.value)
                  }
                  placeholder="e.g., Q3 Culture Audit"
                  autoFocus
                />
              </FormGroup>
            </ConfirmationModalContent>

            <ConfirmationModalFooter>
              <ConfirmationCancelButton
                onClick={() => setShowEditNameModal(false)}
              >
                Cancel
              </ConfirmationCancelButton>
              <ConfirmationDeleteButton onClick={submitEditName}>
                Update Name
              </ConfirmationDeleteButton>
            </ConfirmationModalFooter>
          </ConfirmationModalContainer>
        </ConfirmationModalOverlay>
      )}

      {/* Custom Reminder Modal */}
      {!isSurveyClosed && showCustomReminderModal && (
        <ConfirmationModalOverlay
          onClick={() => setShowCustomReminderModal(false)}
        >
          <ReminderModalContainer onClick={(e) => e.stopPropagation()}>
            <ConfirmationModalHeader>
              <ConfirmationModalTitle>
                Send a Custom Message
              </ConfirmationModalTitle>
              <CloseButton onClick={() => setShowCustomReminderModal(false)}>
                ×
              </CloseButton>
            </ConfirmationModalHeader>

            <ReminderModalContent>
              <ReminderFormGroup>
                <ReminderLabel>Subject Line</ReminderLabel>
                <ReminderInput
                  type="text"
                  value={customReminderData.subject}
                  onChange={(e) =>
                    handleCustomReminderChange("subject", e.target.value)
                  }
                  placeholder="Enter email subject..."
                />
              </ReminderFormGroup>

              <ReminderFormGroup>
                <ReminderLabel>Email Body</ReminderLabel>
                <CompactTextEditorWrapper>
                  <TextEditor
                    value={customReminderData.body}
                    onChange={(content) =>
                      handleCustomReminderChange("body", content)
                    }
                  />
                </CompactTextEditorWrapper>
              </ReminderFormGroup>
              <SendOptionsSection>
                <SendOptionTitle>Delivery Options</SendOptionTitle>
                <SendOptionsToggle>
                  <SendOptionButton
                    active={!customReminderData.sendNow}
                    onClick={() => handleCustomReminderChange("sendNow", false)}
                  >
                    Schedule for Later
                  </SendOptionButton>
                  <SendOptionButton
                    active={customReminderData.sendNow}
                    onClick={() => handleCustomReminderChange("sendNow", true)}
                  >
                    Send Now
                  </SendOptionButton>
                </SendOptionsToggle>
              </SendOptionsSection>

              {!customReminderData.sendNow && (
                <DateTimeSection>
                  <DateTimeInputs>
                    <DateTimeGroup>
                      <DateTimeLabel>Scheduled Date</DateTimeLabel>
                      <DateTimeInput
                        type="date"
                        value={customReminderData.scheduledDate}
                        onChange={(e) =>
                          handleCustomReminderChange(
                            "scheduledDate",
                            e.target.value
                          )
                        }
                        min={new Date().toISOString().split("T")[0]}
                      />
                    </DateTimeGroup>
                    <DateTimeGroup>
                      <DateTimeLabel>
                        Scheduled Time
                        {displayTimezone
                          ? ` (${displayTimezone})`
                          : surveyData?.timezone
                          ? ` (${surveyData.timezone})`
                          : ""}
                      </DateTimeLabel>
                      <DateTimeInput
                        type="time"
                        value={customReminderData.scheduledTime}
                        onChange={(e) =>
                          handleCustomReminderChange(
                            "scheduledTime",
                            e.target.value
                          )
                        }
                      />
                    </DateTimeGroup>
                  </DateTimeInputs>
                </DateTimeSection>
              )}
            </ReminderModalContent>

            <ConfirmationModalFooter>
              <ConfirmationCancelButton
                onClick={() => setShowCustomReminderModal(false)}
              >
                Cancel
              </ConfirmationCancelButton>
              <PrimaryButton onClick={submitCustomReminder}>
                {customReminderData.sendNow ? "Send Now" : "Send Message"}
              </PrimaryButton>
            </ConfirmationModalFooter>
          </ReminderModalContainer>
        </ConfirmationModalOverlay>
      )}

      {/* Send Reminder Confirmation Modal */}
      {!isSurveyClosed && showSendReminderModal && (
        <ConfirmationModalOverlay
          onClick={() => setShowSendReminderModal(false)}
        >
          <ConfirmationModalContainer onClick={(e) => e.stopPropagation()}>
            <ConfirmationModalHeader>
              <ConfirmationModalTitle>Send Reminder</ConfirmationModalTitle>
            </ConfirmationModalHeader>

            <ConfirmationModalContent>
              <ConfirmationModalText>
                Are you sure you want to send an email reminder to all
                respondents who have not completed their surveys?
              </ConfirmationModalText>
              <ConfirmationModalSubtext>
                This will send a reminder email to{" "}
                <strong>
                  {surveyData.pendingResponses} pending recipients
                </strong>
                .
              </ConfirmationModalSubtext>
            </ConfirmationModalContent>

            <ConfirmationModalFooter>
              <ConfirmationCancelButton
                onClick={() => setShowSendReminderModal(false)}
              >
                Cancel
              </ConfirmationCancelButton>
              <ConfirmationDeleteButton onClick={confirmSendReminder}>
                Send Reminder
              </ConfirmationDeleteButton>
            </ConfirmationModalFooter>
          </ConfirmationModalContainer>
        </ConfirmationModalOverlay>
      )}

      {/* Cancel Survey Confirmation Modal */}
      {!isSurveyClosed && showCancelSurveyModal && (
        <ModalOverlay onClick={cancelBusy ? undefined : closeCancelSurveyModal}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Cancel this survey?</ModalTitle>
            <ModalBody>
              This will permanently remove the scheduled survey{" "}
              <strong>{surveyData.title}</strong> before it starts. This action
              cannot be undone.
            </ModalBody>
            <ModalActions>
              <Button disabled={cancelBusy} onClick={closeCancelSurveyModal}>
                Keep Survey
              </Button>
              <PrimaryButton
                disabled={cancelBusy}
                onClick={confirmCancelSurvey}
              >
                {cancelBusy ? "Cancelling..." : "Yes, Cancel Survey"}
              </PrimaryButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}

      {/* Custom Email Status Modal */}
      {!isSurveyClosed && showEmailStatusModal && (
        <ConfirmationModalOverlay
          onClick={() => setShowEmailStatusModal(false)}
        >
          <EmailStatusModalContainer onClick={(e) => e.stopPropagation()}>
            <ConfirmationModalHeader>
              <ConfirmationModalTitle>
                Email Status History
              </ConfirmationModalTitle>
              <CloseButton onClick={() => setShowEmailStatusModal(false)}>
                ×
              </CloseButton>
            </ConfirmationModalHeader>

            <EmailStatusModalContent>
              {/* Scheduled Emails Section */}
              <EmailStatusSection>
                <EmailStatusSectionTitle>
                  📅 Scheduled Emails
                </EmailStatusSectionTitle>
                {get_debrief_emails?.filter((email) => email?.status < 3)
                  .length > 0 ? (
                  <EmailStatusTable>
                    <EmailStatusTableHeader>
                      <EmailStatusTableHeaderCell>
                        Subject
                      </EmailStatusTableHeaderCell>
                      <EmailStatusTableHeaderCell>
                        Scheduled For
                      </EmailStatusTableHeaderCell>
                      <EmailStatusTableHeaderCell>
                        Actions
                      </EmailStatusTableHeaderCell>
                    </EmailStatusTableHeader>
                    <EmailStatusTableBody>
                      {get_debrief_emails
                        ?.filter((email) => email?.status < 3)
                        .map((email, index) => (
                          <EmailStatusTableRow key={index}>
                            <EmailStatusTableCell>
                              <EmailSubject>
                                {email?.content?.subject || "No Subject"}
                              </EmailSubject>
                            </EmailStatusTableCell>
                            <EmailStatusTableCell>
                              <EmailDate>
                                {email?.scheduled_for
                                  ? format(
                                      new Date(email.scheduled_for),
                                      "MMM d, yyyy 'at' h:mm a"
                                    )
                                  : format(
                                      new Date(email.updated_at),
                                      "MMM d, yyyy 'at' h:mm a"
                                    )}
                              </EmailDate>
                            </EmailStatusTableCell>
                            <EmailStatusTableCell>
                              <DeleteEmailButton
                                onClick={() =>
                                  handleDeleteCustomEmail(email.id)
                                }
                                title="Delete email"
                              >
                                <DeleteIcon />
                              </DeleteEmailButton>
                            </EmailStatusTableCell>
                          </EmailStatusTableRow>
                        ))}
                    </EmailStatusTableBody>
                  </EmailStatusTable>
                ) : (
                  <EmptyEmailState>
                    <EmptyEmailIcon>📬</EmptyEmailIcon>
                    <EmptyEmailText>No scheduled emails found</EmptyEmailText>
                  </EmptyEmailState>
                )}
              </EmailStatusSection>

              {/* Email History Section */}
              <EmailStatusSection>
                <EmailStatusSectionTitle>
                  📬 Email History
                </EmailStatusSectionTitle>
                {get_debrief_emails?.some(
                  (email) => email?.response?.length > 0
                ) ? (
                  <EmailHistoryContainer>
                    {get_debrief_emails.map((email, emailIndex) =>
                      email?.response?.map((response, responseIndex) => {
                        const deliveryMessage =
                          typeof response?.message === "string"
                            ? response.message
                            : response?.message?.Message || response?.status;

                        const deliveryErrorCodeRaw =
                          typeof response?.message === "object"
                            ? response?.message?.ErrorCode
                            : null;
                        const deliveryErrorCode =
                          deliveryErrorCodeRaw === null ||
                          deliveryErrorCodeRaw === undefined ||
                          deliveryErrorCodeRaw === ""
                            ? null
                            : Number(deliveryErrorCodeRaw);

                        const deliveryTimestamp =
                          typeof response?.message === "object"
                            ? response?.message?.SubmittedAt
                            : null;

                        return (
                          <EmailHistoryItem
                            key={`${emailIndex}-${responseIndex}`}
                          >
                            <EmailHistoryHeader>
                              <EmailHistoryRecipient>
                                {response?.employee?.first_name}{" "}
                                {response?.employee?.last_name}
                              </EmailHistoryRecipient>
                              <EmailHistoryDate>
                                {format(
                                  new Date(
                                    deliveryTimestamp || email.updated_at
                                  ),
                                  "MMM d, yyyy 'at' h:mm a"
                                )}
                              </EmailHistoryDate>
                            </EmailHistoryHeader>
                            <EmailHistoryDetails>
                              <EmailHistoryDetail>
                                <EmailHistoryLabel>Email:</EmailHistoryLabel>
                                <EmailHistoryValue>
                                  {response?.employee?.email}
                                </EmailHistoryValue>
                              </EmailHistoryDetail>
                              <EmailHistoryDetail>
                                <EmailHistoryLabel>Status:</EmailHistoryLabel>
                                <EmailStatusBadge status={response?.status}>
                                  {response?.status || "Unknown"}
                                </EmailStatusBadge>
                              </EmailHistoryDetail>
                              <EmailHistoryDetail>
                                <EmailHistoryLabel>
                                  Email Status:
                                </EmailHistoryLabel>
                                <EmailHistoryValue>
                                  {response?.employee?.email_status_text || "N/A"}
                                </EmailHistoryValue>
                              </EmailHistoryDetail>
                              {email?.content?.subject && (
                                <EmailHistoryDetail>
                                  <EmailHistoryLabel>Subject:</EmailHistoryLabel>
                                  <EmailHistoryValue>
                                    {email.content.subject}
                                  </EmailHistoryValue>
                                </EmailHistoryDetail>
                              )}
                              {email?.content?.body && (
                                <EmailHistoryDetail>
                                  <EmailHistoryLabel>Message:</EmailHistoryLabel>
                                  <EmailHistoryMessage
                                    dangerouslySetInnerHTML={{
                                      __html: email.content.body,
                                    }}
                                  />
                                </EmailHistoryDetail>
                              )}
                              {deliveryMessage && (
                                <EmailHistoryDetail>
                                  <EmailHistoryLabel>Delivery:</EmailHistoryLabel>
                                  <EmailHistoryValue>
                                    {deliveryMessage}
                                    {Number.isFinite(deliveryErrorCode) &&
                                      deliveryErrorCode !== 0 &&
                                      ` (Error ${deliveryErrorCode})`}
                                  </EmailHistoryValue>
                                </EmailHistoryDetail>
                              )}
                            </EmailHistoryDetails>
                          </EmailHistoryItem>
                        );
                      })
                    )}
                  </EmailHistoryContainer>
                ) : (
                  <EmptyEmailState>
                    <EmptyEmailIcon>📧</EmptyEmailIcon>
                    <EmptyEmailText>No email history found</EmptyEmailText>
                  </EmptyEmailState>
                )}
              </EmailStatusSection>
            </EmailStatusModalContent>

            <ConfirmationModalFooter>
              <ConfirmationCancelButton
                onClick={() => setShowEmailStatusModal(false)}
              >
                Close
              </ConfirmationCancelButton>
            </ConfirmationModalFooter>
          </EmailStatusModalContainer>
        </ConfirmationModalOverlay>
      )}

      <GenerateCsvReportModal
        open={showCsvModal}
        onClose={() => setShowCsvModal(false)}
        recipients={allRecipients}
        surveyStructure={get_survey_structure}
        surveyTitle={surveyData?.title || surveyData?.name || surveyData?.surveyStructureName || "Survey"}
        loading={csvModalLoading}
      />

      {/* Compliance Report Modal */}
      {(showComplianceModal || !isSurveyClosed) && (
        <ComplianceReportModal
          open={showComplianceModal}
          setOpen={setShowComplianceModal}
          data={allRecipients}
          get_survey_structure={get_survey_structure}
          loading={categoriesLoading}
        />
      )}

      {/* Public Link Modal */}
      {surveyData?.rawData?.include_members && (
        <PublicLink
          token={surveyData?.rawData?.survey_id}
          open={showPublicLink}
          setOpen={setShowPublicLink}
        />
      )}
    </Container>
  );
};

// Wrap with ScheduleProvider
const ManageActiveSurvey = () => {
  const { id } = useParams();
  return (
    <ScheduleProvider>
      <ManageActiveSurveyContent key={id || 'no-id'} />
    </ScheduleProvider>
  );
};

export default ManageActiveSurvey;

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  margin-bottom: 60px;
  animation: fadeIn 0.6s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Compact wrapper for custom reminder WYSIWYG editor
const CompactTextEditorWrapper = styled.div`
  & > div { min-height: 300px !important; margin-top: 10px; }
  .wrapperClassName { min-height: 140px !important; }
  .editorClassName { min-height: 80px !important; padding: 10px; }
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 7px;
`;

const HeaderText = styled.div``;

const Title = styled.h1`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 24px;
  color: #414141;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #476dfa;
  border: none;
  border-radius: 7px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

const OptionsMenuWrapper = styled.div`
  position: relative;
  z-index: 2;
  overflow: visible;
`;

// Floating menu rendered via portal (positioned with `position: fixed`)
const FloatingMenu = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 6px 0;
  min-width: 200px;
  z-index: 10000;
`;

const OptionsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 7px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #c0c0c0;
  }
`;

const OptionsMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 6px 0;
  min-width: 200px;
  z-index: 9999;
`;

const OptionsMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 16px;
  background: none;
  border: none;
  text-align: left;
  font-family: "Poppins";
  font-size: 13px;
  color: ${(props) => (props.danger ? "#dc3545" : "#414141")};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: ${(props) => (props.danger ? "#fff5f5" : "#f8f9fa")};
  }
`;

const OptionsMenuDivider = styled.div`
  height: 1px;
  background: #e0e0e0;
  margin: 6px 0;
`;

const SurveyDetailsSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 24px;
`;

const SurveyDetailsCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
`;

const CardTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0;
`;

const CardContent = styled.div`
  padding: 20px;
`;

const ScheduleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const ScheduleItem = styled.div``;

const ScheduleLabel = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #7e7e7e;
  margin-bottom: 4px;
`;

const ScheduleValue = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
`;

const StatsCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  padding: 16px;
  text-align: center;
`;

const StatsLoadingCard = styled(StatCard)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 120px;
`;

const StatValue = styled.div`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 28px;
  color: #476dfa;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #7e7e7e;
`;

const RecipientsSection = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: visible;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
`;

const TableHeaderLeft = styled.div``;

const TableHeaderRight = styled.div``;

const TableTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0;
`;

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  display: flex;
  align-items: center;
  color: #7e7e7e;
`;

const SearchInput = styled.input`
  padding: 8px 12px 8px 36px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  width: 240px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  overflow-y: visible;
  position: relative;
`;

const TableLoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  backdrop-filter: blur(1px);
`;

const TableLoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid #f8f9fa;
  border-top: 3px solid #476dfa;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 8px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const TableLoadingText = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #7e7e7e;
`;

const RecipientsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  opacity: ${(props) => (props.loading ? 0.6 : 1)};
  pointer-events: ${(props) => (props.loading ? 'none' : 'auto')};
  transition: opacity 0.2s;
`;

const TableHead = styled.thead`
  background: #fafafa;
`;

const TableHeadRow = styled.tr``;

const TableHeadCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  color: #414141;
  border-bottom: 1px solid #e0e0e0;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  transition: background-color 0.2s;
  animation: slideInRow 0.3s ease-out;
  animation-fill-mode: both;

  &:hover {
    background: #f0f4ff;
  }

  @keyframes slideInRow {
    from {
      opacity: 0;
      transform: translateX(-8px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  ${(props) =>
    props.index &&
    `
    animation-delay: ${props.index * 0.02}s;
  `}
`;

const TableCell = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
`;

const RecipientName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
`;

const RecipientEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
`;

const ReminderCount = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  text-align: center;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;

  ${(props) => {
    switch (props.status) {
      case "completed":
        return `
          background: #e8f5e8;
          color: #2e7d2e;
        `;
      case "in_progress":
        return `
          background: #e3f2fd;
          color: #1976d2;
        `;
      case "pending":
        return `
          background: #fff4e6;
          color: #cc8500;
        `;
      case "cancelled":
        return `
          background: #ffebee;
          color: #c62828;
        `;
      case "incomplete":
        return `
          background: #fce4ec;
          color: #ad1457;
        `;
      case "email_bounced":
        return `
          background: #fef3f4;
          color: #c62828;
        `;
      default:
        return `
          background: #f0f0f0;
          color: #666;
        `;
    }
  }}
`;

const SubmittedTime = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const ActionButtonSmall = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid ${(props) => (props.danger ? "#dc3545" : "#e0e0e0")};
  background: ${(props) => (props.danger ? "#fff5f5" : "white")};
  border-radius: 6px;
  color: ${(props) => (props.danger ? "#dc3545" : "#7e7e7e")};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.danger ? "#dc3545" : "#f8f9fa")};
    color: ${(props) => (props.danger ? "white" : "#414141")};
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  border-top: 1px solid #e0e0e0;
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  background: ${(props) => (props.disabled ? "#f8f9fa" : "#476dfa")};
  border: none;
  border-radius: 6px;
  color: ${(props) => (props.disabled ? "#aaa" : "white")};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
  }
`;

const PaginationInfo = styled.div`
  display: inline-flex;
  align-items: center;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  gap: 4px;
`;

const PageJumpInput = styled.input`
  width: 50px;
  padding: 4px 6px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #414141;
  text-align: center;
  outline: none;
  transition: border-color 0.2s;
  display: inline-block;

  &:focus {
    border-color: #476dfa;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 18px;
  color: #414141;
  margin: 0 0 6px 0;
`;

const EmptyText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  margin: 0;
`;

// New styled components for sorting, filtering, and confirmation modal
const TableHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StatusFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusFilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const SortableTableHeadCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  color: #414141;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  user-select: none;

  &:hover {
    background: #f8f9fa;
  }

  svg {
    margin-left: 4px;
    opacity: ${(props) => (props.active ? 1 : 0.3)};
    transform: ${(props) =>
      props.active && props.direction === "desc"
        ? "rotate(180deg)"
        : "rotate(0deg)"};
    transition: all 0.2s;
  }

  ${(props) =>
    props.active &&
    `
    background: #f0f4ff;
    color: #476dfa;
  `}
`;

// Confirmation Modal Styled Components
const ConfirmationModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 20px;
`;

const ConfirmationModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease-out;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ConfirmationModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;

  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

const ConfirmationModalTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 18px;
  color: #414141;
  margin: 0;
`;

const ConfirmationModalContent = styled.div`
  padding: 20px 24px;
`;

const ConfirmationModalText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #414141;
  margin: 0 0 12px 0;
  line-height: 1.5;

  strong {
    font-weight: 600;
    color: #dc3545;
  }
`;

const ConfirmationModalSubtext = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  margin: 0;
  line-height: 1.4;
`;

const ConfirmationModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px 24px;
  border-top: 1px solid #f0f0f0;
`;

const ConfirmationCancelButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 7px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #c0c0c0;
  }
`;

const ConfirmationDeleteButton = styled.button`
  padding: 10px 20px;
  background: #dc3545;
  border: none;
  border-radius: 7px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #c82333;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px 8px;
  font-size: 20px;
  cursor: pointer;
  color: #7e7e7e;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f0f0f0;
    color: #414141;
  }
`;

// Extend Deadline Modal Styles
const QuickExtendSection = styled.div`
  margin: 20px 0;
`;

const QuickExtendTitle = styled.h4`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin: 0 0 12px 0;
`;

const QuickExtendButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const QuickExtendButton = styled.button`
  padding: 8px 14px;
  background: ${(props) => (props.active ? "#476dfa" : "white")};
  border: 1px solid ${(props) => (props.active ? "#476dfa" : "#e0e0e0")};
  border-radius: 6px;
  color: ${(props) => (props.active ? "white" : "#414141")};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.active ? "#3d5ce6" : "#f8f9fa")};
    border-color: ${(props) => (props.active ? "#3d5ce6" : "#c0c0c0")};
  }
`;

const DateTimeSection = styled.div`
  margin: 20px 0;
`;

const DateTimeTitle = styled.h4`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin: 0 0 12px 0;
`;

const DateTimeInputs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const DateTimeGroup = styled.div``;

const DateTimeLabel = styled.label`
  display: block;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #414141;
  margin-bottom: 6px;
`;

const DateTimeInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

// Custom Reminder Modal Styles
const ReminderModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 720px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease-out;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ReminderModalContent = styled.div`
  padding: 20px 24px;
  max-height: 70vh;
  overflow-y: auto;
`;

const ReminderFormGroup = styled.div`
  margin-bottom: 20px;
`;

const ReminderLabel = styled.label`
  display: block;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 8px;
`;

const ReminderInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const ReminderTextarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const SendOptionsSection = styled.div`
  margin-bottom: 20px;
`;

const SendOptionTitle = styled.h4`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin: 0 0 12px 0;
`;

const SendOptionsToggle = styled.div`
  display: flex;
  gap: 2px;
  background: #f8f9fa;
  padding: 4px;
  border-radius: 8px;
  width: fit-content;
`;

const SendOptionButton = styled.button`
  padding: 8px 16px;
  background: ${(props) => (props.active ? "#476dfa" : "transparent")};
  border: none;
  border-radius: 6px;
  color: ${(props) => (props.active ? "white" : "#7e7e7e")};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${(props) => (props.active ? "#3d5ce6" : "#e9ecef")};
  }
`;

// Loading and Error State Components
const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f8f9fa;
  border-top: 4px solid #476dfa;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 16px;
  color: #7e7e7e;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
`;

const ErrorTitle = styled.h2`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 24px;
  color: #414141;
  margin: 0 0 8px 0;
`;

const ErrorText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 16px;
  color: #7e7e7e;
  margin: 0 0 24px 0;
`;

// Modal and Banner Components for Cancel Survey
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(18,18,18,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalCard = styled.div`
  width: 420px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  padding: 20px;
`;

const ModalTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #2a3039;
`;

const ModalBody = styled.p`
  margin: 0 0 18px 0;
  color: #444;
  font-size: 14px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const Button = styled.button`
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  padding: 8px 12px;
  background: #fff;
  cursor: pointer;
`;

const DangerButton = styled(Button)`
  border-color: #f0c1c1;
  background: #fef2f2;
  color: #b42318;
`;

const PrimaryButton = styled(Button)`
  background: #476dfa;
  color: #fff;
  border-color: #476dfa;
`;

const InfoBanner = styled.div`
  background: #fff8e6;
  border: 1px solid #ffe0a3;
  border-radius: 10px;
  padding: 14px 16px;
  margin: 12px 0 16px 0;
`;

const BannerTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  color: #7a5200;
`;

const BannerText = styled.div`
  font-size: 13px;
  color: #6a5a34;
`;

const BannerActions = styled.div`
  margin-top: 10px;
`;

const SurveyTypeBadge = styled.div`
  display: inline-block;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  color: #7e7e7e;
  background: #f0f4ff;
  padding: 4px 8px;
  border-radius: 4px;
`;

// Email Status Modal Styled Components
const EmailStatusModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 900px;
  max-height: 80vh;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease-out;
  display: flex;
  flex-direction: column;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const EmailStatusModalContent = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
  max-height: 60vh;
`;


const EmailStatusSection = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EmailStatusSectionTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EmailStatusTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
`;

const EmailStatusTableHeader = styled.thead`
  background: #f8f9fa;
`;

const EmailStatusTableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  color: #414141;
  border-bottom: 1px solid #e0e0e0;
`;

const EmailStatusTableBody = styled.tbody``;

const EmailStatusTableRow = styled.tr`
  &:hover {
    background: #f8f9fa;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const EmailStatusTableCell = styled.td`
  padding: 12px 16px;
  vertical-align: middle;
`;

const EmailSubject = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
`;

const EmailDate = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
`;

const DeleteEmailButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #dc3545;
  background: #fff5f5;
  border-radius: 6px;
  color: #dc3545;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #dc3545;
    color: white;
  }
`;

const EmptyEmailState = styled.div`
  padding: 40px 20px;
  text-align: center;
  background: #fafafa;
  border-radius: 8px;
  border: 2px dashed #e0e0e0;
`;

const EmptyEmailIcon = styled.div`
  font-size: 24px;
  margin-bottom: 8px;
  opacity: 0.6;
`;

const EmptyEmailText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
`;

const EmailHistoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EmailHistoryItem = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const EmailHistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
`;

const EmailHistoryRecipient = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
`;

const EmailHistoryDate = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const EmailHistoryDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmailHistoryDetail = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const EmailHistoryLabel = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #7e7e7e;
  min-width: 80px;
  flex-shrink: 0;
`;

const EmailHistoryValue = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #414141;
  flex: 1;
`;

const EmailHistoryMessage = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #414141;
  background: #f8f9fa;
  padding: 8px;
  border-radius: 4px;
  border-left: 3px solid #476dfa;
  flex: 1;
  word-break: break-word;
  max-width: 400px;
  line-height: 1.5;

  & p {
    margin: 0 0 8px;
  }

  & p:last-child {
    margin-bottom: 0;
  }

  & ul,
  & ol {
    margin: 0 0 8px 16px;
    padding: 0;
  }

  & a {
    color: #476dfa;
    text-decoration: underline;
  }
`;

const EmailStatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  background: ${(props) => {
    switch (props.status?.toLowerCase()) {
      case 'sent':
      case 'delivered':
        return '#e8f5e8';
      case 'failed':
      case 'bounced':
        return '#ffebee';
      case 'pending':
        return '#fff4e6';
      default:
        return '#f0f0f0';
    }
  }};
  color: ${(props) => {
    switch (props.status?.toLowerCase()) {
      case 'sent':
      case 'delivered':
        return '#2e7d2e';
      case 'failed':
      case 'bounced':
        return '#d32f2f';
      case 'pending':
        return '#cc8500';
      default:
        return '#666';
    }
  }};
`;

// Form Components for Edit Name Modal
const FormGroup = styled.div`
  margin-top: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }

  &::placeholder {
    color: #aaa;
  }
`;
